import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { API } from '@/utils/constants';
import { useAuth } from '@/context/AuthContext';
import { HelpTooltip } from '@/components/HelpTooltip';
import { printJobSticker } from '@/utils/sticker';
import { pairZebraPrinter, getPairedZebra, webUsbSupported } from '@/utils/usbPrinter';
import { BrowserPrintSettings } from './components/BrowserPrintSettings';
// Extracted tab components
import { SMSSettingsTab, CustomerEmailTemplatesEditor, SecurityAuditTab } from './components';
import { TwoFactorPanel } from '@/components/security/TwoFactorPanel';

/**
 * Sortable Item Component for drag-and-drop reordering
 */
const SortableItem = ({ id, item, onRemove, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="item"
      {...attributes}
      {...listeners}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: '1.2rem', opacity: 0.5 }}>☰</span>
        <span>{item}</span>
      </span>
      <Button 
        size="sm" 
        variant="ghost" 
        onClick={(e) => {
          e.stopPropagation();
          onRemove(index);
        }}
      >
        Remove
      </Button>
    </div>
  );
};

/**
 * Referral Partners Manager
 * -----------------------------------------------------------------------
 * Manages the standalone `referral_partners` collection. Distinct from the
 * `referral_sources` array on Settings — see backend docstring for context.
 * Supports search, single add, single delete (soft), rename, and bulk paste.
 */
const ReferralPartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const load = async (q = '') => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/referral-partners`, { params: { search: q } });
      setPartners(res.data || []);
    } catch (err) {
      console.error('Load partners failed:', err);
      toast.error('Failed to load referral partners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(''); }, []);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/referral-partners`, { name });
      toast.success(`Added "${name}"`);
      setNewName('');
      load(search);
    } catch (err) {
      toast.error('Failed to add partner');
    }
  };

  const handleDelete = async (partner) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove "${partner.name}" from the partner list?\n\nExisting jobs already tagged with this partner will keep the name for history.`)) return;
    try {
      await axios.delete(`${API}/referral-partners/${partner.id}`);
      toast.success('Partner removed');
      load(search);
    } catch (err) {
      toast.error('Failed to remove partner');
    }
  };

  const startRename = (p) => {
    setRenameId(p.id);
    setRenameValue(p.name);
  };
  const cancelRename = () => { setRenameId(null); setRenameValue(''); };
  const commitRename = async () => {
    const clean = renameValue.trim();
    if (!clean) return;
    try {
      await axios.put(`${API}/referral-partners/${renameId}`, { name: clean });
      toast.success('Partner renamed');
      cancelRename();
      load(search);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to rename partner';
      toast.error(msg);
    }
  };

  const handleBulkAdd = async () => {
    const names = bulkText.split('\n').map((s) => s.trim()).filter(Boolean);
    if (names.length === 0) { toast.error('Paste one partner per line first'); return; }
    try {
      const res = await axios.post(`${API}/referral-partners/bulk`, { names });
      toast.success(`Added ${res.data.created}, skipped ${res.data.skipped_duplicates} duplicate${res.data.skipped_duplicates === 1 ? '' : 's'}`);
      setBulkText('');
      setBulkOpen(false);
      load(search);
    } catch (err) {
      toast.error('Bulk add failed');
    }
  };

  const handleSearch = (v) => {
    setSearch(v);
    load(v);
  };

  return (
    <div className="dropdown-section" style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '2px solid #e5e7eb' }}>
      <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        Referral Partners
        <HelpTooltip
          content="A specific business or person who sends customers your way — e.g. DRC Computers, ABC Store. Distinct from the coarse 'How did you find us?' channel above. Appears as an autocomplete on the Add Job form, and shows on the Reports page."
          side="right"
        />
      </h3>
      <p className="section-description" style={{ marginBottom: '0.75rem' }}>
        The list grows automatically as you type new names on the Add Job form. Manage the master list here or bulk-paste to seed it.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <Input
          placeholder="Search partners…"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          data-testid="partners-search-input"
          style={{ maxWidth: '260px' }}
        />
        <Button variant="outline" onClick={() => setBulkOpen((v) => !v)} data-testid="bulk-partners-toggle">
          {bulkOpen ? 'Cancel Bulk Paste' : 'Bulk Paste'}
        </Button>
      </div>

      {bulkOpen && (
        <div style={{ padding: '0.75rem', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '0.75rem' }}>
          <Label style={{ fontWeight: 600, marginBottom: '0.35rem', display: 'block' }}>Paste one partner per line</Label>
          <Textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={"DRC Computers\nABC Store\nXYZ Repairs"}
            rows={6}
            data-testid="bulk-partners-textarea"
            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
          />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <Button onClick={handleBulkAdd} data-testid="bulk-partners-submit">
              Add {bulkText.split('\n').filter((l) => l.trim()).length} partner(s)
            </Button>
          </div>
        </div>
      )}

      <div className="items-list" style={{ maxHeight: '340px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }} data-testid="partners-list">
        {loading && partners.length === 0 && (
          <div style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.85rem' }}>Loading…</div>
        )}
        {!loading && partners.length === 0 && (
          <div style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.85rem' }}>
            {search ? `No partners match "${search}". Add one below.` : 'No partners yet — start typing on the Add Job form or paste a list here.'}
          </div>
        )}
        {partners.map((p) => (
          <div
            key={p.id}
            className="item"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.55rem 0.75rem', borderBottom: '1px solid #f1f5f9' }}
            data-testid={`partner-row-${p.id}`}
          >
            {renameId === p.id ? (
              <>
                <Input
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') cancelRename(); }}
                  autoFocus
                  style={{ maxWidth: '280px' }}
                  data-testid={`partner-rename-input-${p.id}`}
                />
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <Button size="sm" onClick={commitRename} data-testid={`partner-rename-save-${p.id}`}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={cancelRename}>Cancel</Button>
                </div>
              </>
            ) : (
              <>
                <span>{p.name}</span>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <Button size="sm" variant="ghost" onClick={() => startRename(p)} data-testid={`partner-rename-btn-${p.id}`}>Rename</Button>
                  <Button size="sm" variant="ghost" style={{ color: '#dc2626' }} onClick={() => handleDelete(p)} data-testid={`partner-delete-btn-${p.id}`}>Remove</Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="add-item" style={{ marginTop: '0.75rem' }}>
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Add new partner (e.g. DRC Computers)"
          onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
          data-testid="new-partner-input"
        />
        <Button onClick={handleAdd} data-testid="add-partner-button">Add</Button>
      </div>
    </div>
  );
};


/**
 * QuickEntryTemplatesManager
 * ---------------------------------------------------------------------------
 * Generic CRUD UI for either `settings.expense_quick_templates` or
 * `settings.purchase_quick_templates`. Given a list of templates + a schema
 * (which fields each template carries), renders a table + inline add form.
 *
 * Props:
 *   - title, description       Section heading + explainer
 *   - fieldKey                 Settings key to save under (e.g. 'expense_quick_templates')
 *   - templates                Current array of template objects
 *   - fields                   Array of { key, label, type, options?, placeholder? }
 *                              type = 'text' | 'select'
 *   - onSave(nextArray)        Persists via /api/settings
 *   - testIdPrefix             Prefix for data-testid attributes (unique across managers)
 */
const QuickEntryTemplatesManager = ({
  title,
  description,
  templates,
  fields,
  onSave,
  testIdPrefix,
}) => {
  const emptyDraft = () => Object.fromEntries(fields.map(f => [f.key, '']));
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());

  const list = Array.isArray(templates) ? templates : [];

  const handleAdd = async () => {
    // Require at least a name
    if (!(draft.name || '').trim()) {
      toast.error('Template name is required');
      return;
    }
    const cleaned = Object.fromEntries(
      fields.map(f => [f.key, (draft[f.key] || '').trim()])
    );
    const next = [...list, cleaned];
    try {
      await onSave(next);
      setDraft(emptyDraft());
      setShowAdd(false);
    } catch {
      // onSave shows its own error toast
    }
  };

  const handleRemove = async (idx) => {
    const next = list.filter((_, i) => i !== idx);
    try { await onSave(next); } catch {/* toast by onSave */}
  };

  return (
    <div className="dropdown-section" data-testid={`${testIdPrefix}-manager`}>
      <h3>{title}</h3>
      <p className="section-description">{description}</p>

      {list.length > 0 && (
        <div style={{ marginTop: '0.5rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', overflow: 'hidden' }}>
          <Table>
            <TableHeader>
              <TableRow>
                {fields.map(f => (
                  <TableHead key={f.key}>{f.label}</TableHead>
                ))}
                <TableHead style={{ width: '80px', textAlign: 'right' }}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((tpl, idx) => (
                <TableRow key={idx} data-testid={`${testIdPrefix}-row-${idx}`}>
                  {fields.map(f => (
                    <TableCell key={f.key} style={{ fontSize: '0.85rem' }}>
                      {tpl[f.key] || <span style={{ color: '#94a3b8' }}>—</span>}
                    </TableCell>
                  ))}
                  <TableCell style={{ textAlign: 'right' }}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemove(idx)}
                      style={{ color: '#dc2626' }}
                      data-testid={`${testIdPrefix}-remove-${idx}`}
                    >
                      ✕
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {showAdd ? (
        <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {fields.map(f => (
            <div key={f.key}>
              <Label style={{ fontSize: '0.8rem', marginBottom: '0.25rem', display: 'block' }}>{f.label}{f.key === 'name' ? ' *' : ''}</Label>
              {f.type === 'select' ? (
                <select
                  value={draft[f.key] || ''}
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                  data-testid={`${testIdPrefix}-draft-${f.key}`}
                  style={{ width: '100%', padding: '0.4rem 0.5rem', borderRadius: '0.375rem', border: '1px solid #cbd5e1', backgroundColor: '#ffffff', fontSize: '0.85rem' }}
                >
                  <option value="">— select —</option>
                  {(f.options || []).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <Input
                  value={draft[f.key] || ''}
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                  placeholder={f.placeholder || ''}
                  data-testid={`${testIdPrefix}-draft-${f.key}`}
                />
              )}
            </div>
          ))}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
            <Button onClick={handleAdd} data-testid={`${testIdPrefix}-save-btn`}>Add Template</Button>
            <Button variant="outline" onClick={() => { setShowAdd(false); setDraft(emptyDraft()); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" onClick={() => setShowAdd(true)} style={{ marginTop: '0.75rem' }} data-testid={`${testIdPrefix}-add-btn`}>
          + Add Template
        </Button>
      )}
    </div>
  );
};


const SettingsPage = () => {
  const { user: currentUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [counter, setCounter] = useState(null);
  const [editing, setEditing] = useState(null);
  const [newValue, setNewValue] = useState('');
  const [newExpenseCode, setNewExpenseCode] = useState('');
  
  // User management state
  const [users, setUsers] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    full_name: '',
    role: 'user'
  });
  
  // Backup & Restore state
  const [backupSettings, setBackupSettings] = useState(null);
  const [backupList, setBackupList] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [selectedBackupId, setSelectedBackupId] = useState(null);
  const [selectiveRestore, setSelectiveRestore] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const fileInputRef = useRef(null);

  // Google Drive integration state
  const [driveStatus, setDriveStatus] = useState({ connected: false, folder_name: 'Emergent Backup', auto_upload: true });
  const [driveFolderInput, setDriveFolderInput] = useState('Emergent Backup');
  const [driveSaving, setDriveSaving] = useState(false);
  const [driveUploadingId, setDriveUploadingId] = useState(null);
  
  // Database Reset state
  const [dbStats, setDbStats] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  
  // Address Autocomplete (Google Places) state
  const [addressSettings, setAddressSettings] = useState(null);
  const [addressApiKey, setAddressApiKey] = useState('');
  const [savingAddressSettings, setSavingAddressSettings] = useState(false);
  const [testingAddressConnection, setTestingAddressConnection] = useState(false);
  
  // Email Test Connection state
  const [testingEmailConnection, setTestingEmailConnection] = useState(false);
  
  // IP Access Control state
  const [ipAccessSettings, setIpAccessSettings] = useState({
    enabled: false,
    allowed_ips: [],
    bypass_enabled: false,
    bypass_code: '',
    remember_device: true,
    remember_days: 30
  });
  const [currentIP, setCurrentIP] = useState('');
  const [newIPAddress, setNewIPAddress] = useState('');
  const [newIPLabel, setNewIPLabel] = useState('');
  const [savingIPSettings, setSavingIPSettings] = useState(false);

  // Jim2 Cutover state
  const [cutoverStatus, setCutoverStatus] = useState(null);   // { customers: {present, size, modified}, jobs: {...}, confirmation_phrase }
  const [cutoverUploadingKind, setCutoverUploadingKind] = useState(null); // 'customers' | 'jobs' | null
  const [cutoverConfirmText, setCutoverConfirmText] = useState('');
  const [cutoverRunning, setCutoverRunning] = useState(false);
  const [cutoverResult, setCutoverResult] = useState(null);
  
  const BACKUP_COLLECTIONS = ['jobs', 'customers', 'suppliers', 'stock_items', 'purchases', 'expenses', 'settings', 'users', 'counters'];
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchSettings();
    fetchCounter();
    fetchAddressSettings();
    if (currentUser?.role === 'admin') {
      fetchUsers();
      fetchBackupSettings();
      fetchBackupList();
      fetchDbStats();
      fetchIPAccessSettings();
      fetchCurrentIP();
      fetchDriveStatus();
      fetchCutoverStatus();
    }
  }, [currentUser]);

  // Resume polling cutover run-status on mount (handles refresh mid-migration)
  useEffect(() => {
    if (currentUser?.role !== 'admin') return;
    let cancelled = false;
    (async () => {
      try {
        const sr = await axios.get(`${API}/admin/cutover/run-status`);
        if (cancelled || !sr.data?.exists) return;
        setCutoverResult(sr.data);
        if (!sr.data.running) return;
        // Active run in progress (likely from a previous tab/session) — resume polling
        setCutoverRunning(true);
        const startTime = Date.now();
        const MAX_POLL_MS = 60 * 60 * 1000;
        while (!cancelled && Date.now() - startTime < MAX_POLL_MS) {
          await new Promise((res) => setTimeout(res, 3000));
          try {
            const sr2 = await axios.get(`${API}/admin/cutover/run-status`);
            if (cancelled) return;
            setCutoverResult(sr2.data);
            if (sr2.data && sr2.data.running === false) {
              if (sr2.data.ok) toast.success('Cutover migration completed successfully');
              else toast.error(`Cutover failed at step: ${sr2.data.failed_at || 'unknown'}`);
              setCutoverRunning(false);
              break;
            }
          } catch (_) { /* keep polling through transient errors */ }
        }
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Handle return from Google OAuth callback (URL params)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('drive_connected') === 'true') {
      toast.success('Google Drive connected!');
      fetchDriveStatus();
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('drive_error')) {
      toast.error(`Google Drive connect failed: ${params.get('drive_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchDriveStatus = async () => {
    try {
      const response = await axios.get(`${API}/drive/status`);
      setDriveStatus(response.data);
      if (response.data.folder_name) setDriveFolderInput(response.data.folder_name);
    } catch (error) {
      console.error('Error fetching Drive status:', error);
    }
  };

  const handleDriveConnect = async () => {
    try {
      const response = await axios.get(`${API}/drive/connect`);
      // Google's OAuth screen cannot be displayed inside an iframe (X-Frame-Options: DENY).
      // Open in a new tab so the consent flow runs in a top-level browsing context.
      const newWin = window.open(response.data.authorization_url, '_blank', 'noopener,noreferrer');
      if (!newWin) {
        toast.error('Pop-up blocked. Please allow pop-ups for this site and try again.');
        return;
      }
      toast.info('Complete the connection in the new tab, then come back and refresh.');
      // Poll for connection status every 3 seconds so the UI updates as soon as user completes OAuth
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const r = await axios.get(`${API}/drive/status`);
          if (r.data.connected) {
            clearInterval(poll);
            setDriveStatus(r.data);
            if (r.data.folder_name) setDriveFolderInput(r.data.folder_name);
            toast.success('Google Drive connected!');
          }
        } catch (e) { /* ignore */ }
        if (attempts > 60) clearInterval(poll); // stop after 3 minutes
      }, 3000);
    } catch (error) {
      toast.error('Failed to start Drive connect flow');
    }
  };

  const handleDriveDisconnect = async () => {
    if (!window.confirm('Disconnect Google Drive? Future backups will no longer be uploaded.')) return;
    try {
      await axios.post(`${API}/drive/disconnect`);
      toast.success('Google Drive disconnected');
      fetchDriveStatus();
    } catch (error) {
      toast.error('Failed to disconnect Drive');
    }
  };

  const handleDriveConfigSave = async () => {
    setDriveSaving(true);
    try {
      await axios.put(`${API}/drive/config`, {
        folder_name: driveFolderInput,
        auto_upload: driveStatus.auto_upload,
      });
      toast.success('Drive settings saved');
      fetchDriveStatus();
    } catch (error) {
      toast.error('Failed to save Drive settings');
    } finally {
      setDriveSaving(false);
    }
  };

  const handleManualDriveUpload = async (backupId) => {
    setDriveUploadingId(backupId);
    try {
      const response = await axios.post(`${API}/drive/upload/${backupId}`);
      toast.success('Uploaded to Google Drive');
      fetchBackupList();
      if (response.data.link) {
        // Optionally show link
        console.log('Drive link:', response.data.link);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Upload failed';
      toast.error(`Drive upload failed: ${detail}`);
    } finally {
      setDriveUploadingId(null);
    }
  };

  // ---------- Jim2 Cutover ----------
  const fetchCutoverStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/cutover/status`);
      setCutoverStatus(r.data);
    } catch (error) {
      // Silently ignore — only admins can hit this; surfaced in UI as "not loaded".
    }
  };

  const handleCutoverUpload = async (kind, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Please pick a .xlsx file');
      return;
    }
    setCutoverUploadingKind(kind);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API}/admin/cutover/upload?kind=${kind}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${kind === 'customers' ? 'Customers' : 'Jobs'} file uploaded`);
      fetchCutoverStatus();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Upload failed';
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setCutoverUploadingKind(null);
    }
  };

  const handleCutoverClear = async () => {
    if (!window.confirm('Delete the staged customers_v2.xlsx and jobs.xlsx so you can upload fresh files?')) {
      return;
    }
    try {
      const r = await axios.delete(`${API}/admin/cutover/clear`);
      toast.success(`Cleared ${r.data.deleted.length} file(s)`);
      fetchCutoverStatus();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Clear failed';
      toast.error(`Clear failed: ${detail}`);
    }
  };

  const handleCutoverRun = async (mode = 'full') => {
    const phrase = cutoverStatus?.confirmation_phrase || 'WIPE AND IMPORT';
    if (cutoverConfirmText !== phrase) {
      toast.error(`Type "${phrase}" exactly to confirm`);
      return;
    }
    if (!cutoverStatus?.customers?.present || (mode === 'full' && !cutoverStatus?.jobs?.present)) {
      toast.error('Upload required files first');
      return;
    }
    const confirmMsg = mode === 'backfills_only'
      ? 'Re-run ONLY the 4 backfill steps (referral, customer types, job types, revenue dates)? Your customers and jobs will NOT be wiped — only their classifications will be re-applied. Continue?'
      : 'This will WIPE all customers, jobs, purchases, expenses, stock and re-import from the uploaded Excel files. Continue?';
    if (!window.confirm(confirmMsg)) {
      return;
    }
    setCutoverRunning(true);
    setCutoverResult(null);
    try {
      // 1. Clear any previous run state so the UI shows fresh progress
      try {
        await axios.post(`${API}/admin/cutover/run-status/reset`);
      } catch (_) {
        // ignore — refused only if a run is already in progress, surfaced below
      }

      // 2. Kick off the background run — returns immediately
      const endpoint = mode === 'backfills_only' ? '/admin/cutover/run-backfills-only' : '/admin/cutover/run';
      await axios.post(`${API}${endpoint}`, { confirmation: cutoverConfirmText });
      toast.success(mode === 'backfills_only' ? 'Backfills started — polling for progress…' : 'Migration started — polling for progress…');

      // 3. Poll /run-status every 3s until running=false (or 1h safety cap)
      const startTime = Date.now();
      const MAX_POLL_MS = 60 * 60 * 1000;
      while (Date.now() - startTime < MAX_POLL_MS) {
        await new Promise((res) => setTimeout(res, 3000));
        try {
          const sr = await axios.get(`${API}/admin/cutover/run-status`);
          setCutoverResult(sr.data);
          if (sr.data && sr.data.running === false && sr.data.exists) {
            if (sr.data.ok) {
              toast.success(mode === 'backfills_only' ? 'Backfills completed successfully' : 'Cutover migration completed successfully');
            } else {
              toast.error(`Failed at step: ${sr.data.failed_at || 'unknown'}`);
            }
            break;
          }
        } catch (pollErr) {
          // Transient network errors during polling are non-fatal — keep trying
          console.warn('poll error', pollErr);
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Migration failed';
      toast.error(`Migration failed: ${detail}`);
      setCutoverResult({ ok: false, error: detail });
    } finally {
      setCutoverRunning(false);
      setCutoverConfirmText('');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    }
  };

  const fetchCounter = async () => {
    try {
      const response = await axios.get(`${API}/counter`);
      setCounter(response.data);
    } catch (error) {
      console.error('Error fetching counter:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  // IP Access Control functions
  const fetchIPAccessSettings = async () => {
    try {
      const response = await axios.get(`${API}/ip-access/settings`);
      setIpAccessSettings(response.data);
    } catch (error) {
      console.error('Error fetching IP access settings:', error);
    }
  };

  const fetchCurrentIP = async () => {
    try {
      const response = await axios.get(`${API}/ip-access/my-ip`);
      setCurrentIP(response.data.ip);
    } catch (error) {
      console.error('Error fetching current IP:', error);
    }
  };

  const handleSaveIPSettings = async () => {
    setSavingIPSettings(true);
    try {
      await axios.put(`${API}/ip-access/settings`, ipAccessSettings);
      toast.success('IP access settings saved');
    } catch (error) {
      console.error('Error saving IP settings:', error);
      toast.error('Failed to save IP access settings');
    } finally {
      setSavingIPSettings(false);
    }
  };

  const handleAddIP = async () => {
    if (!newIPAddress.trim()) {
      toast.error('Please enter an IP address');
      return;
    }
    try {
      await axios.post(`${API}/ip-access/add-ip`, {
        ip: newIPAddress.trim(),
        label: newIPLabel.trim() || `IP added on ${new Date().toLocaleDateString()}`
      });
      toast.success('IP address added');
      setNewIPAddress('');
      setNewIPLabel('');
      fetchIPAccessSettings();
    } catch (error) {
      console.error('Error adding IP:', error);
      toast.error(error.response?.data?.detail || 'Failed to add IP address');
    }
  };

  const handleRemoveIP = async (ip) => {
    try {
      await axios.delete(`${API}/ip-access/remove-ip/${encodeURIComponent(ip)}`);
      toast.success('IP address removed');
      fetchIPAccessSettings();
    } catch (error) {
      console.error('Error removing IP:', error);
      toast.error('Failed to remove IP address');
    }
  };

  const handleAddCurrentIP = () => {
    if (currentIP) {
      setNewIPAddress(currentIP);
      setNewIPLabel('My Current Location');
    }
  };

  // Backup & Restore functions
  const fetchBackupSettings = async () => {
    try {
      const response = await axios.get(`${API}/backup/settings`);
      setBackupSettings(response.data);
    } catch (error) {
      console.error('Error fetching backup settings:', error);
    }
  };

  const fetchBackupList = async () => {
    try {
      const response = await axios.get(`${API}/backup/list`);
      setBackupList(response.data);
    } catch (error) {
      console.error('Error fetching backup list:', error);
    }
  };

  const handleUpdateBackupSettings = async (updates) => {
    try {
      await axios.put(`${API}/backup/settings`, updates);
      toast.success('Backup settings updated');
      fetchBackupSettings();
    } catch (error) {
      console.error('Error updating backup settings:', error);
      toast.error('Failed to update backup settings');
    }
  };

  const handleCreateBackup = async (sendEmail = false) => {
    try {
      setBackupLoading(true);
      const response = await axios.post(`${API}/backup/create?send_email=${sendEmail}`, null, { timeout: 15000 });
      // Feb 2026 — backend now returns { queued: true } and runs the backup as
      // a background task. Poll the backup list for up to 3 minutes to detect
      // the new entry (or a failure flag on settings).
      if (response.data?.queued) {
        toast.info('Backup queued — this can take up to a minute…', { duration: 6000 });
        const startedAt = Date.now();
        const initialSettings = await axios.get(`${API}/backup/settings`);
        const initialLastTime = initialSettings.data?.last_backup_time || '';
        let landed = false;
        for (let i = 0; i < 60 && !landed; i += 1) {
          await new Promise(r => setTimeout(r, 3000));
          try {
            const sRes = await axios.get(`${API}/backup/settings`);
            const s = sRes.data || {};
            if (s.last_backup_status === 'failed') {
              throw new Error(s.last_backup_error || 'Backup failed');
            }
            if (s.last_backup_status === 'success' && s.last_backup_time && s.last_backup_time !== initialLastTime) {
              landed = true;
              break;
            }
          } catch (err) {
            if (String(err.message || '').startsWith('Backup failed')) throw err;
            // Ignore transient poll errors and keep polling.
          }
          if (Date.now() - startedAt > 180000) break;
        }
        if (landed) {
          toast.success('Backup created successfully!');
        } else {
          toast.warning('Backup is still running — refresh in a moment to see it in the list.', { duration: 8000 });
        }
      } else {
        toast.success('Backup created successfully!');
      }
      fetchBackupList();
      fetchBackupSettings();
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error(`Failed to create backup: ${error?.response?.data?.detail || error.message}`);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDownloadBackup = async (backupId = null) => {
    try {
      setBackupLoading(true);

      let targetBackupId = backupId;
      let targetFilename = null;

      if (!backupId) {
        // "Download Now" — stream the freshly-generated zip directly from the
        // server. Skips creating a persistent backup record so it works even
        // when the DB is large enough to trip the Kubernetes ingress timeout
        // on the /backup/create path (which is now background-only).
        toast.info('Building & downloading fresh backup — this may take a minute for large databases…', { duration: 8000 });
        const dl0 = await axios.get(`${API}/backup/download-latest`, {
          responseType: 'blob',
          timeout: 600000, // 10 min
        });
        const blob0 = new Blob([dl0.data], { type: 'application/zip' });
        const url0 = URL.createObjectURL(blob0);
        const a0 = document.createElement('a');
        a0.href = url0;
        a0.download = `backup_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
        document.body.appendChild(a0);
        a0.click();
        document.body.removeChild(a0);
        setTimeout(() => URL.revokeObjectURL(url0), 30000);
        toast.success(`Backup downloaded: ${a0.download}`);
        return;
      }

      // ── Bulletproof download path: pull the ZIP as an authenticated Blob and
      // save it via the browser's download API. Works in every browser +
      // production Kubernetes ingress + Cloudflare without depending on:
      //   • server-generated download tokens (production may buffer/route these differently)
      //   • popup blockers (window.open can be blocked)
      //   • cross-origin anchor .download attribute quirks
      toast.info('Downloading backup — this may take a few seconds…');
      const dl = await axios.get(`${API}/backup/download/${targetBackupId}`, {
        responseType: 'blob',
        // Explicit long timeout for large backups over slow production links
        timeout: 300000, // 5 min
      });

      const blob = new Blob([dl.data], { type: 'application/zip' });
      const objUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objUrl;
      link.download = targetFilename || `backup_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Free the blob memory after a short delay so the download can complete
      setTimeout(() => URL.revokeObjectURL(objUrl), 30000);

      toast.success(backupId
        ? `Downloaded ${link.download} — check your downloads folder.`
        : `Backup created and downloaded: ${link.download}`);
    } catch (error) {
      console.error('Error downloading backup:', error);
      // Fallback path — try the legacy token+link approach so the user still
      // has SOMETHING that works if the blob path is blocked by proxy/CSP.
      try {
        const targetBackupId = backupId; // may be null on 'download now'
        if (!targetBackupId) throw error; // no fallback for uncreated backup
        const tokenResponse = await axios.post(`${API}/backup/generate-download-token/${targetBackupId}`);
        const downloadUrl = `${API}/backup/public-download/${tokenResponse.data.download_token}`;
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        toast.info('Blob download failed — opened public link in a new tab as fallback.');
      } catch (e2) {
        toast.error(
          error.response?.data?.detail
            || error.message
            || 'Download failed. Try the "📋 Copy Link" button and paste the URL into a new tab.'
        );
      }
    } finally {
      setBackupLoading(false);
    }
  };

  const handleCopyDownloadLink = async (backupId) => {
    try {
      // Generate a temporary download token
      const tokenResponse = await axios.post(`${API}/backup/generate-download-token/${backupId}`);
      const downloadToken = tokenResponse.data.download_token;
      const downloadUrl = `${API}/backup/public-download/${downloadToken}`;
      
      // Try clipboard first, fallback to prompt if blocked
      try {
        await navigator.clipboard.writeText(downloadUrl);
        toast.success('Download link copied! Paste in a new tab to download. Link expires in 5 minutes.');
      } catch (clipboardError) {
        // Clipboard may be blocked in sandboxed environments - show prompt with the link
        window.prompt('Copy this download link (expires in 5 minutes):', downloadUrl);
        toast.info('Copy the link from the dialog. It expires in 5 minutes.');
      }
    } catch (error) {
      console.error('Error generating download link:', error);
      toast.error('Failed to generate download link');
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to delete this backup?')) return;
    
    try {
      await axios.delete(`${API}/backup/${backupId}`);
      toast.success('Backup deleted');
      fetchBackupList();
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error('Failed to delete backup');
    }
  };

  const handleRestoreFromServer = async (backupId) => {
    setSelectedBackupId(backupId);
    setShowRestoreConfirm(true);
  };

  const confirmRestore = async () => {
    try {
      setBackupLoading(true);
      
      const collectionsToRestore = selectiveRestore && selectedCollections.length > 0 
        ? selectedCollections 
        : null;
      
      if (selectedBackupId) {
        // Restore from server backup
        await axios.post(`${API}/backup/restore`, {
          backup_id: selectedBackupId,
          collections: collectionsToRestore,
          confirm: true
        });
      } else if (restoreFile) {
        // Restore from uploaded file
        const formData = new FormData();
        formData.append('file', restoreFile);
        if (collectionsToRestore) {
          formData.append('collections', collectionsToRestore.join(','));
        }
        await axios.post(`${API}/backup/restore-upload`, formData);
      }
      
      toast.success('Restore completed! Please refresh the page.');
      setShowRestoreConfirm(false);
      setSelectedBackupId(null);
      setRestoreFile(null);
      setSelectiveRestore(false);
      setSelectedCollections([]);
      
      // Refresh settings
      setTimeout(() => {
        fetchSettings();
        fetchBackupSettings();
        fetchBackupList();
      }, 1000);
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.zip')) {
      setRestoreFile(file);
      setSelectedBackupId(null);
      setShowRestoreConfirm(true);
    } else {
      toast.error('Please select a valid .zip backup file');
    }
  };

  const handleTestBackupEmail = async () => {
    try {
      setBackupLoading(true);
      await axios.post(`${API}/backup/test-email`);
      toast.success('Test backup email sent!');
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleTestBackupNotification = async () => {
    try {
      setBackupLoading(true);
      const { data } = await axios.post(`${API}/backup/test-notification`);
      toast.success(data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test notification');
    } finally {
      setBackupLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Database Reset functions
  const fetchDbStats = async () => {
    try {
      const response = await axios.get(`${API}/database/stats`);
      setDbStats(response.data);
    } catch (error) {
      console.error('Error fetching database stats:', error);
    }
  };

  // Address Autocomplete (Google Places) functions
  const fetchAddressSettings = async () => {
    try {
      const response = await axios.get(`${API}/address/settings`);
      setAddressSettings(response.data.google_places);
    } catch (error) {
      console.error('Error fetching address settings:', error);
    }
  };

  const handleSaveAddressApiKey = async () => {
    if (!addressApiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }
    
    setSavingAddressSettings(true);
    try {
      await axios.put(`${API}/address/settings`, null, {
        params: { api_key: addressApiKey, enabled: true }
      });
      toast.success('Google Places API key saved successfully');
      setAddressApiKey('');
      fetchAddressSettings();
    } catch (error) {
      console.error('Error saving API key:', error);
      toast.error('Failed to save API key');
    } finally {
      setSavingAddressSettings(false);
    }
  };

  const handleTestAddressConnection = async () => {
    setTestingAddressConnection(true);
    try {
      const response = await axios.post(`${API}/address/test-connection`);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingAddressConnection(false);
    }
  };

  const handleTestEmailConnection = async () => {
    setTestingEmailConnection(true);
    try {
      const response = await axios.post(`${API}/settings/test-email`);
      toast.success(response.data.message, { duration: 6000 });
    } catch (error) {
      console.error('Error testing email connection:', error);
      toast.error(error.response?.data?.detail || 'Email connection test failed', { duration: 10000 });
    } finally {
      setTestingEmailConnection(false);
    }
  };

  const handleDatabaseReset = async () => {
    if (resetConfirmText !== 'RESET') {
      toast.error('Please type RESET to confirm');
      return;
    }
    if (!resetPassword) {
      toast.error('Please enter your admin password');
      return;
    }

    try {
      setResetLoading(true);
      const response = await axios.post(`${API}/database/reset`, {
        password: resetPassword,
        confirm_text: resetConfirmText,
        keep_settings: true,
        keep_users: true,
        reset_job_counter: true
      });
      
      toast.success('Database reset completed! All business data has been cleared.');
      setShowResetConfirm(false);
      setResetPassword('');
      setResetConfirmText('');
      
      // Refresh stats
      fetchDbStats();
      fetchBackupList();
    } catch (error) {
      console.error('Error resetting database:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset database');
    } finally {
      setResetLoading(false);
    }
  };

  // Format date to Australian format (dd/MM/yyyy HH:mm) in local timezone
  const formatBackupDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, userForm);
      toast.success('User created successfully!');
      setShowUserForm(false);
      setUserForm({ username: '', password: '', full_name: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        full_name: userForm.full_name,
        role: userForm.role
      };
      if (userForm.password) {
        updateData.password = userForm.password;
      }
      await axios.put(`${API}/users/${editingUser}`, updateData);
      toast.success('User updated successfully!');
      setEditingUser(null);
      setUserForm({ username: '', password: '', full_name: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted successfully!');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const startEditUser = (user) => {
    setEditingUser(user.id);
    setUserForm({
      username: user.username,
      password: '',
      full_name: user.full_name,
      role: user.role
    });
  };

  const cancelUserEdit = () => {
    setEditingUser(null);
    setShowUserForm(false);
    setUserForm({ username: '', password: '', full_name: '', role: 'user' });
  };

  // Debounce timer for settings updates
  const debounceTimerRef = useRef(null);

  const updateSettings = (field, value) => {
    // Update local state immediately for instant UI feedback
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer to save to backend after user stops typing
    debounceTimerRef.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/settings`, { [field]: value });
        toast.success('Settings saved', { duration: 2000 });
      } catch (error) {
        console.error('Error updating settings:', error);
        let errorMessage = 'Failed to save settings';
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (typeof detail === 'string') {
            errorMessage = detail;
          } else if (Array.isArray(detail)) {
            errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          }
        }
        toast.error(errorMessage);
        // Revert to server state on error
        fetchSettings();
      }
    }, 800); // Wait 800ms after user stops typing before saving
  };

  const handleDragEnd = async (event, field) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = parseInt(active.id.split('-')[1]);
    const newIndex = parseInt(over.id.split('-')[1]);
    
    const items = [...settings[field]];
    const reorderedItems = arrayMove(items, oldIndex, newIndex);
    
    // Optimistic update
    setSettings({ ...settings, [field]: reorderedItems });
    
    // Save to backend
    try {
      await axios.put(`${API}/settings`, { [field]: reorderedItems });
      toast.success('Order updated!');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to save new order');
      // Revert on error
      fetchSettings();
    }
  };

  const updateCounter = async (value) => {
    try {
      await axios.put(`${API}/counter?current_value=${value}`);
      toast.success('Job counter updated successfully!');
      fetchCounter();
    } catch (error) {
      console.error('Error updating counter:', error);
      toast.error('Failed to update counter');
    }
  };

  const addItem = (field) => {
    if (newValue.trim()) {
      const updatedList = [...settings[field], newValue.trim()];
      updateSettings(field, updatedList);
      setNewValue('');
      setEditing(null);
    }
  };

  const removeItem = (field, index) => {
    const updatedList = settings[field].filter((_, i) => i !== index);
    updateSettings(field, updatedList);
  };

  if (!settings || !counter) {
    return <div className="loading">Loading settings...</div>;
  }

  return (
    <div className="settings-container">
      <Card>
        <CardHeader>
          <CardTitle>Application Settings</CardTitle>
          <CardDescription>Manage dropdown values and field names</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="dropdowns" className="settings-tabs">
            <TabsList>
              <TabsTrigger value="dropdowns" data-testid="dropdowns-tab">Dropdown Values</TabsTrigger>
              <TabsTrigger value="business" data-testid="business-tab">Business Info</TabsTrigger>
              <TabsTrigger value="email" data-testid="email-tab">Email Config</TabsTrigger>
              <TabsTrigger value="templates" data-testid="templates-tab">Email Templates</TabsTrigger>
              <TabsTrigger value="colors" data-testid="colors-tab">Status Colors</TabsTrigger>
              <TabsTrigger value="counter" data-testid="counter-tab">Job Counter</TabsTrigger>
              <TabsTrigger value="security" data-testid="security-tab">Security</TabsTrigger>
              <TabsTrigger value="address" data-testid="address-tab">Address Lookup</TabsTrigger>
              <TabsTrigger value="sms" data-testid="sms-tab">SMS (Twilio)</TabsTrigger>
              <TabsTrigger value="notifications" data-testid="notifications-tab">Notifications</TabsTrigger>
              {currentUser?.role === 'admin' && (
                <>
                  <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
                  <TabsTrigger value="audit" data-testid="audit-tab">🔒 Security Audit</TabsTrigger>
                  <TabsTrigger value="backup" data-testid="backup-tab">Backup & Restore</TabsTrigger>
                  <TabsTrigger value="cutover" data-testid="cutover-tab">Jim2 Cutover</TabsTrigger>
                </>
              )}
            </TabsList>

            <TabsContent value="dropdowns">
              <div className="dropdown-settings">
                <div className="dropdown-section">
                  <h3>
                    Job Types
                    <HelpTooltip 
                      content="Define the types of work your business offers. These appear in the Job Type dropdown when creating or editing jobs. Drag to reorder." 
                      side="right"
                    />
                  </h3>
                  <p className="section-description">Drag to reorder</p>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(event, 'job_types')}
                  >
                    <SortableContext
                      items={settings.job_types.map((type, idx) => `job_types-${idx}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="items-list">
                        {settings.job_types.map((type, index) => (
                          <SortableItem
                            key={`job_types-${index}`}
                            id={`job_types-${index}`}
                            item={type}
                            index={index}
                            onRemove={(idx) => removeItem('job_types', idx)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {editing === 'job_types' ? (
                    <div className="add-item">
                      <Input
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder="New job type"
                        onKeyPress={(e) => e.key === 'Enter' && addItem('job_types')}
                        data-testid="new-job-type-input"
                      />
                      <Button onClick={() => addItem('job_types')} data-testid="add-job-type-button">Add</Button>
                      <Button variant="outline" onClick={() => { setEditing(null); setNewValue(''); }}>Cancel</Button>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setEditing('job_types')} data-testid="add-new-job-type-button">Add New</Button>
                  )}
                </div>

                <div className="dropdown-section">
                  <h3>Brands</h3>
                  <p className="section-description">Drag to reorder</p>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(event, 'brands')}
                  >
                    <SortableContext
                      items={settings.brands.map((brand, idx) => `brands-${idx}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="items-list">
                        {settings.brands.map((brand, index) => (
                          <SortableItem
                            key={`brands-${index}`}
                            id={`brands-${index}`}
                            item={brand}
                            index={index}
                            onRemove={(idx) => removeItem('brands', idx)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {editing === 'brands' ? (
                    <div className="add-item">
                      <Input
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder="New brand"
                        onKeyPress={(e) => e.key === 'Enter' && addItem('brands')}
                        data-testid="new-brand-input"
                      />
                      <Button onClick={() => addItem('brands')} data-testid="add-brand-button">Add</Button>
                      <Button variant="outline" onClick={() => { setEditing(null); setNewValue(''); }}>Cancel</Button>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setEditing('brands')} data-testid="add-new-brand-button">Add New</Button>
                  )}
                </div>

                <div className="dropdown-section">
                  <h3>Statuses</h3>
                  <p className="section-description">Drag to reorder</p>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(event, 'statuses')}
                  >
                    <SortableContext
                      items={settings.statuses.map((status, idx) => `statuses-${idx}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="items-list">
                        {settings.statuses.map((status, index) => (
                          <SortableItem
                            key={`statuses-${index}`}
                            id={`statuses-${index}`}
                            item={status}
                            index={index}
                            onRemove={(idx) => removeItem('statuses', idx)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {editing === 'statuses' ? (
                    <div className="add-item">
                      <Input
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder="New status"
                        onKeyPress={(e) => e.key === 'Enter' && addItem('statuses')}
                        data-testid="new-status-input"
                      />
                      <Button onClick={() => addItem('statuses')} data-testid="add-status-button">Add</Button>
                      <Button variant="outline" onClick={() => { setEditing(null); setNewValue(''); }}>Cancel</Button>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setEditing('statuses')} data-testid="add-new-status-button">Add New</Button>
                  )}
                </div>

                <div className="dropdown-section">
                  <h3>Referral Sources (How did you find us?)</h3>
                  <p className="section-description">Drag to reorder</p>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(event, 'referral_sources')}
                  >
                    <SortableContext
                      items={(settings.referral_sources || []).map((source, idx) => `referral_sources-${idx}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="items-list">
                        {(settings.referral_sources || ['Google', 'Referral', 'Partner', 'Walk-in']).map((source, index) => (
                          <SortableItem
                            key={`referral_sources-${index}`}
                            id={`referral_sources-${index}`}
                            item={source}
                            index={index}
                            onRemove={(idx) => removeItem('referral_sources', idx)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {editing === 'referral_sources' ? (
                    <div className="add-item">
                      <Input
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder="New referral source"
                        onKeyPress={(e) => e.key === 'Enter' && addItem('referral_sources')}
                        data-testid="new-referral-input"
                      />
                      <Button onClick={() => addItem('referral_sources')} data-testid="add-referral-button">Add</Button>
                      <Button variant="outline" onClick={() => { setEditing(null); setNewValue(''); }}>Cancel</Button>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setEditing('referral_sources')} data-testid="add-new-referral-button">Add New</Button>
                  )}
                </div>

                {/* Referral Partners — specific named partners (e.g. DRC Computers) who send us customers */}
                <ReferralPartnersManager />

                <div className="dropdown-section">
                  <h3>Customer Types</h3>
                  <p className="section-description">Drag to reorder</p>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(event, 'customer_types')}
                  >
                    <SortableContext
                      items={(settings.customer_types || []).map((type, idx) => `customer_types-${idx}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="items-list">
                        {(settings.customer_types || ['End User', 'Partner']).map((type, index) => (
                          <SortableItem
                            key={`customer_types-${index}`}
                            id={`customer_types-${index}`}
                            item={type}
                            index={index}
                            onRemove={(idx) => removeItem('customer_types', idx)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {editing === 'customer_types' ? (
                    <div className="add-item">
                      <Input
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder="New customer type"
                        onKeyPress={(e) => e.key === 'Enter' && addItem('customer_types')}
                      />
                      <Button onClick={() => addItem('customer_types')}>Add</Button>
                      <Button variant="outline" onClick={() => { setEditing(null); setNewValue(''); }}>Cancel</Button>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setEditing('customer_types')}>Add New</Button>
                  )}
                </div>

                <div className="dropdown-section">
                  <h3>Accessory Options</h3>
                  <p className="section-description">Common accessories shown as one-tap chips on the New Job form. Drag to reorder.</p>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(event, 'accessory_options')}
                  >
                    <SortableContext
                      items={(settings.accessory_options || []).map((opt, idx) => `accessory_options-${idx}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="items-list">
                        {(settings.accessory_options || []).map((opt, index) => (
                          <SortableItem
                            key={`accessory_options-${index}`}
                            id={`accessory_options-${index}`}
                            item={opt}
                            index={index}
                            onRemove={(idx) => removeItem('accessory_options', idx)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {editing === 'accessory_options' ? (
                    <div className="add-item">
                      <Input
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder="New accessory option (e.g. Adapter)"
                        onKeyPress={(e) => e.key === 'Enter' && addItem('accessory_options')}
                        data-testid="new-accessory-option-input"
                      />
                      <Button onClick={() => addItem('accessory_options')} data-testid="add-accessory-option-confirm-btn">Add</Button>
                      <Button variant="outline" onClick={() => { setEditing(null); setNewValue(''); }}>Cancel</Button>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setEditing('accessory_options')} data-testid="add-accessory-option-btn">Add New</Button>
                  )}
                </div>

                <div className="dropdown-section">
                  <h3>Expense Categories with Account Codes</h3>
                  <p className="section-description">Categories for tracking business expenses. Account codes help organize your Chart of Accounts. <strong>Changes auto-save.</strong></p>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead style={{ width: '100px' }}>Code</TableHead>
                          <TableHead>Category Name</TableHead>
                          <TableHead style={{ width: '80px' }}>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(settings.expense_categories_with_codes || []).map((cat, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Input
                                value={cat.code || ''}
                                onChange={(e) => {
                                  const updated = [...(settings.expense_categories_with_codes || [])];
                                  updated[index] = { ...updated[index], code: e.target.value };
                                  setSettings({ ...settings, expense_categories_with_codes: updated });
                                }}
                                onBlur={async () => {
                                  // Auto-save on blur and re-sort
                                  try {
                                    const updated = [...settings.expense_categories_with_codes];
                                    // Sort by code numerically
                                    updated.sort((a, b) => {
                                      const codeA = parseInt(a.code) || 99999;
                                      const codeB = parseInt(b.code) || 99999;
                                      return codeA - codeB;
                                    });
                                    const oldCategories = updated.map(c => c.name);
                                    setSettings({ ...settings, expense_categories_with_codes: updated, expense_categories: oldCategories });
                                    await axios.put(`${API}/settings`, { 
                                      expense_categories_with_codes: updated,
                                      expense_categories: oldCategories
                                    });
                                  } catch (error) {
                                    console.error('Failed to save:', error);
                                    toast.error('Failed to save changes');
                                  }
                                }}
                                placeholder="6000"
                                style={{ width: '80px', fontFamily: 'monospace' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={cat.name || ''}
                                onChange={(e) => {
                                  const updated = [...(settings.expense_categories_with_codes || [])];
                                  updated[index] = { ...updated[index], name: e.target.value };
                                  const oldCategories = updated.map(c => c.name);
                                  setSettings({ ...settings, expense_categories_with_codes: updated, expense_categories: oldCategories });
                                }}
                                onBlur={async () => {
                                  // Auto-save on blur
                                  try {
                                    const updated = settings.expense_categories_with_codes;
                                    const oldCategories = updated.map(c => c.name);
                                    await axios.put(`${API}/settings`, { 
                                      expense_categories_with_codes: updated,
                                      expense_categories: oldCategories
                                    });
                                  } catch (error) {
                                    console.error('Failed to save:', error);
                                    toast.error('Failed to save changes');
                                  }
                                }}
                                placeholder="Category name"
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  const updated = (settings.expense_categories_with_codes || []).filter((_, i) => i !== index);
                                  const oldCategories = updated.map(c => c.name);
                                  setSettings({ ...settings, expense_categories_with_codes: updated, expense_categories: oldCategories });
                                  // Auto-save deletion
                                  try {
                                    await axios.put(`${API}/settings`, { 
                                      expense_categories_with_codes: updated,
                                      expense_categories: oldCategories
                                    });
                                    toast.success('Category deleted');
                                  } catch (error) {
                                    console.error('Failed to save:', error);
                                    toast.error('Failed to delete category');
                                  }
                                }}
                                style={{ color: '#dc2626' }}
                              >
                                ✕
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {editing === 'expense_categories' ? (
                    <div className="add-item" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <Input
                        value={newExpenseCode}
                        onChange={(e) => setNewExpenseCode(e.target.value)}
                        placeholder="Code (e.g. 6100)"
                        style={{ width: '120px', fontFamily: 'monospace' }}
                        data-testid="new-expense-code-input"
                      />
                      <Input
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder="Category name (e.g. Vehicle - Fuel)"
                        style={{ flex: 1 }}
                        onKeyPress={async (e) => {
                          if (e.key === 'Enter' && newValue) {
                            const updated = [...(settings.expense_categories_with_codes || []), { name: newValue, code: newExpenseCode }];
                            // Sort by code numerically
                            updated.sort((a, b) => {
                              const codeA = parseInt(a.code) || 99999;
                              const codeB = parseInt(b.code) || 99999;
                              return codeA - codeB;
                            });
                            const oldCategories = updated.map(c => c.name);
                            setSettings({ ...settings, expense_categories_with_codes: updated, expense_categories: oldCategories });
                            // Auto-save
                            try {
                              await axios.put(`${API}/settings`, { 
                                expense_categories_with_codes: updated,
                                expense_categories: oldCategories
                              });
                              toast.success('Category added');
                            } catch (error) {
                              toast.error('Failed to save category');
                            }
                            setNewValue('');
                            setNewExpenseCode('');
                            setEditing(null);
                          }
                        }}
                        data-testid="new-expense-category-input"
                      />
                      <Button 
                        onClick={async () => {
                          if (newValue) {
                            const updated = [...(settings.expense_categories_with_codes || []), { name: newValue, code: newExpenseCode }];
                            // Sort by code numerically
                            updated.sort((a, b) => {
                              const codeA = parseInt(a.code) || 99999;
                              const codeB = parseInt(b.code) || 99999;
                              return codeA - codeB;
                            });
                            const oldCategories = updated.map(c => c.name);
                            setSettings({ ...settings, expense_categories_with_codes: updated, expense_categories: oldCategories });
                            // Auto-save
                            try {
                              await axios.put(`${API}/settings`, { 
                                expense_categories_with_codes: updated,
                                expense_categories: oldCategories
                              });
                              toast.success('Category added');
                            } catch (error) {
                              toast.error('Failed to save category');
                            }
                            setNewValue('');
                            setNewExpenseCode('');
                            setEditing(null);
                          }
                        }} 
                        data-testid="add-expense-category-button"
                      >
                        Add
                      </Button>
                      <Button variant="outline" onClick={() => { setEditing(null); setNewValue(''); setNewExpenseCode(''); }}>Cancel</Button>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setEditing('expense_categories')} data-testid="add-new-expense-category-button">
                      + Add New Category
                    </Button>
                  )}
                  
                  <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f0f9ff', borderRadius: '0.5rem', border: '1px solid #bfdbfe' }}>
                    <p style={{ fontSize: '0.75rem', color: '#1e40af', margin: 0 }}>
                      <strong>💡 Tip:</strong> Use format "Parent - Child" for subcategories (e.g., "Vehicle - Fuel"). 
                      Account codes help organize expenses in your P&L report.
                    </p>
                  </div>
                </div>

                <div className="dropdown-section">
                  <h3>Expense & Purchase Payment Methods</h3>
                  <p className="section-description">Payment methods for money going OUT — expenses and supplier purchases (add American Express, PayPal, etc. here)</p>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(event, 'expense_payment_methods')}
                  >
                    <SortableContext
                      items={(settings.expense_payment_methods || []).map((method, idx) => `expense_payment_methods-${idx}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="items-list">
                        {(settings.expense_payment_methods || []).map((method, index) => (
                          <SortableItem
                            key={`expense_payment_methods-${index}`}
                            id={`expense_payment_methods-${index}`}
                            item={method}
                            index={index}
                            onRemove={(idx) => removeItem('expense_payment_methods', idx)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {editing === 'expense_payment_methods' ? (
                    <div className="add-item">
                      <Input
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder="New payment method"
                        onKeyPress={(e) => e.key === 'Enter' && addItem('expense_payment_methods')}
                        data-testid="new-payment-method-input"
                      />
                      <Button onClick={() => addItem('expense_payment_methods')} data-testid="add-payment-method-button">Add</Button>
                      <Button variant="outline" onClick={() => { setEditing(null); setNewValue(''); }}>Cancel</Button>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setEditing('expense_payment_methods')} data-testid="add-new-payment-method-button">Add New</Button>
                  )}

                  {/* Feb 2026 — Default payment method pre-filled on new
                      Expense and Purchase forms. Operator can still override
                      on each transaction. Leave blank to fall back to the
                      original "Bank Transfer" default. */}
                  <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    <Label htmlFor="default-payment-method" style={{ fontWeight: 600 }}>
                      Default Payment Method
                    </Label>
                    <p className="section-description" style={{ marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                      Pre-fills this method when opening a new Expense or Purchase form. You can still override on each entry.
                    </p>
                    <Select
                      value={settings.default_payment_method || '__none__'}
                      onValueChange={(v) => updateSettings('default_payment_method', v === '__none__' ? '' : v)}
                    >
                      <SelectTrigger id="default-payment-method" data-testid="default-payment-method-select" style={{ maxWidth: '20rem' }}>
                        <SelectValue placeholder="No default (use Bank Transfer)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No default (use Bank Transfer)</SelectItem>
                        {(settings.expense_payment_methods || []).map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Feb 2026 — Customer Payment Methods (money IN). Separate
                    vocabulary from expense/purchase payments so operators can
                    offer PayPal/Stripe/BPAY to customers independent of what
                    they use to pay suppliers. Powers the Payment Method
                    dropdown on the Record Payment form inside each job. */}
                <div className="dropdown-section">
                  <h3>Customer Payment Methods</h3>
                  <p className="section-description">Payment methods for money coming IN from customers — appears in the Payment Method dropdown when you record a payment on a job (add PayPal, Stripe, BPAY, etc. here)</p>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(event, 'incoming_payment_methods')}
                  >
                    <SortableContext
                      items={(settings.incoming_payment_methods || []).map((_, idx) => `incoming_payment_methods-${idx}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="items-list">
                        {(settings.incoming_payment_methods || []).map((method, index) => (
                          <SortableItem
                            key={`incoming_payment_methods-${index}`}
                            id={`incoming_payment_methods-${index}`}
                            item={method}
                            index={index}
                            onRemove={(idx) => removeItem('incoming_payment_methods', idx)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {editing === 'incoming_payment_methods' ? (
                    <div className="add-item">
                      <Input
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder="New customer payment method"
                        onKeyPress={(e) => e.key === 'Enter' && addItem('incoming_payment_methods')}
                        data-testid="new-incoming-payment-method-input"
                      />
                      <Button onClick={() => addItem('incoming_payment_methods')} data-testid="add-incoming-payment-method-button">Add</Button>
                      <Button variant="outline" onClick={() => { setEditing(null); setNewValue(''); }}>Cancel</Button>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setEditing('incoming_payment_methods')} data-testid="add-new-incoming-payment-method-button">Add New</Button>
                  )}
                </div>

                {/* ── Expense Quick Entry Templates ─────────────────────── */}
                <QuickEntryTemplatesManager
                  title="Expense Quick Entry Templates"
                  description="One-click chips that appear at the top of the Expenses page — click to pre-fill the New Expense form with common recurring costs (rent, utilities, fuel, etc.)"
                  templates={settings.expense_quick_templates || []}
                  fields={[
                    { key: 'name', label: 'Button Label', type: 'text', placeholder: 'e.g. Monthly AWS' },
                    { key: 'category', label: 'Category', type: 'select', options: (settings.expense_categories_with_codes || []).map(c => c.name).filter(Boolean) },
                    { key: 'description', label: 'Description', type: 'text', placeholder: 'e.g. AWS Hosting' },
                    { key: 'payment_method', label: 'Payment Method', type: 'select', options: settings.expense_payment_methods || [] },
                  ]}
                  onSave={async (next) => {
                    setSettings({ ...settings, expense_quick_templates: next });
                    try {
                      await axios.put(`${API}/settings`, { expense_quick_templates: next });
                      toast.success('Quick entry templates updated');
                    } catch (e) {
                      toast.error('Failed to save templates');
                      fetchSettings();
                      throw e;
                    }
                  }}
                  testIdPrefix="expense-quick-tpl"
                />

                {/* ── Purchase Quick Entry Templates ────────────────────── */}
                <QuickEntryTemplatesManager
                  title="Purchase Quick Entry Templates"
                  description="One-click chips that appear at the top of the Purchases page — click to pre-fill Record Purchase with common suppliers and payment methods (e.g. Officeworks Restock, Amazon Order)."
                  templates={settings.purchase_quick_templates || []}
                  fields={[
                    { key: 'name', label: 'Button Label', type: 'text', placeholder: 'e.g. Officeworks Restock' },
                    { key: 'supplier_name', label: 'Supplier (name)', type: 'text', placeholder: 'e.g. Officeworks' },
                    { key: 'payment_method', label: 'Payment Method', type: 'select', options: settings.expense_payment_methods || [] },
                    { key: 'notes', label: 'Notes (optional)', type: 'text', placeholder: 'Any default notes' },
                  ]}
                  onSave={async (next) => {
                    setSettings({ ...settings, purchase_quick_templates: next });
                    try {
                      await axios.put(`${API}/settings`, { purchase_quick_templates: next });
                      toast.success('Quick entry templates updated');
                    } catch (e) {
                      toast.error('Failed to save templates');
                      fetchSettings();
                      throw e;
                    }
                  }}
                  testIdPrefix="purchase-quick-tpl"
                />
              </div>
            </TabsContent>

            <TabsContent value="business">
              {settings.business_info ? (
              <div className="business-settings">
                <div className="business-section">
                  <h3>
                    Business Information
                    <HelpTooltip 
                      content="Your business details appear on invoices, quotes, job forms, and emails. Upload a logo for professional branding." 
                      side="right"
                    />
                  </h3>
                  <p className="section-description">This information will appear on all printouts and emails</p>
                  <div className="business-form">
                    <div className="form-field">
                      <Label htmlFor="business-name">Business Name</Label>
                      <Input
                        id="business-name"
                        value={settings.business_info.business_name || ''}
                        onChange={(e) => {
                          const updatedInfo = {...settings.business_info, business_name: e.target.value};
                          updateSettings('business_info', updatedInfo);
                        }}
                        data-testid="business-name-input"
                      />
                    </div>
                    <div className="form-field">
                      <Label htmlFor="business-logo">Business Logo</Label>
                      <input
                        id="business-logo"
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            try {
                              const response = await axios.post(`${API}/upload-logo`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              
                              const logoUrl = `${API}${response.data.logo_url}`;
                              const updatedInfo = {...settings.business_info, logo_url: logoUrl};
                              updateSettings('business_info', updatedInfo);
                              toast.success('Logo uploaded successfully!');
                            } catch (error) {
                              console.error('Error uploading logo:', error);
                              toast.error('Failed to upload logo');
                            }
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem'
                        }}
                      />
                      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                        Select your logo image file. It will appear on invoices and printouts.
                        <br/>
                        Supported formats: PNG, JPG, GIF, WEBP | Recommended size: 200x80 pixels
                      </p>
                      {settings.business_info.logo_url && (
                        <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <div style={{ fontSize: '0.875rem', fontWeight: '600' }}>Current Logo:</div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const updatedInfo = {...settings.business_info, logo_url: ''};
                                updateSettings('business_info', updatedInfo);
                                toast.success('Logo removed');
                              }}
                            >
                              Remove Logo
                            </Button>
                          </div>
                          <img 
                            src={settings.business_info.logo_url} 
                            alt="Business Logo Preview" 
                            style={{ maxWidth: '200px', maxHeight: '80px', border: '1px solid #d1d5db' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div style={{ display: 'none', color: '#dc2626', fontSize: '0.875rem' }}>
                            ⚠️ Failed to load logo. Please re-upload.
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="form-field">
                      <Label htmlFor="business-phone">Phone Number</Label>
                      <Input
                        id="business-phone"
                        value={settings.business_info.phone || ''}
                        onChange={(e) => {
                          const updatedInfo = {...settings.business_info, phone: e.target.value};
                          updateSettings('business_info', updatedInfo);
                        }}
                        data-testid="business-phone-input"
                      />
                    </div>
                    <div className="form-field">
                      <Label htmlFor="business-email">Email Address</Label>
                      <Input
                        id="business-email"
                        type="email"
                        value={settings.business_info.email || ''}
                        onChange={(e) => {
                          const updatedInfo = {...settings.business_info, email: e.target.value};
                          updateSettings('business_info', updatedInfo);
                        }}
                        data-testid="business-email-input"
                      />
                    </div>
                    <div className="form-field">
                      <Label htmlFor="business-website">Website</Label>
                      <Input
                        id="business-website"
                        value={settings.business_info.website || ''}
                        onChange={(e) => {
                          const updatedInfo = {...settings.business_info, website: e.target.value};
                          updateSettings('business_info', updatedInfo);
                        }}
                        data-testid="business-website-input"
                      />
                    </div>
                    <div className="form-field">
                      <Label htmlFor="business-address">Address</Label>
                      <Input
                        id="business-address"
                        value={settings.business_info.address || ''}
                        onChange={(e) => {
                          const updatedInfo = {...settings.business_info, address: e.target.value};
                          updateSettings('business_info', updatedInfo);
                        }}
                        data-testid="business-address-input"
                      />
                    </div>
                    <div className="form-field">
                      <Label htmlFor="business-abn">ABN</Label>
                      <Input
                        id="business-abn"
                        placeholder="12 345 678 901"
                        value={settings.business_info.abn || ''}
                        onChange={(e) => {
                          const updatedInfo = {...settings.business_info, abn: e.target.value};
                          updateSettings('business_info', updatedInfo);
                        }}
                        data-testid="business-abn-input"
                      />
                    </div>
                    <div className="form-field">
                      <Label htmlFor="business-logo">Logo URL (optional)</Label>
                      <Input
                        id="business-logo"
                        placeholder="https://example.com/logo.png"
                        value={settings.business_info.logo_url || ''}
                        onChange={(e) => {
                          const updatedInfo = {...settings.business_info, logo_url: e.target.value};
                          updateSettings('business_info', updatedInfo);
                        }}
                        data-testid="business-logo-input"
                      />
                      <p className="input-hint">Enter a URL to an image of your logo</p>
                    </div>
                  </div>
                </div>

                <div className="business-section">
                  <h3>Overdue Reminder Colour</h3>
                  <p className="section-description">Choose the color for the overdue reminders box on your dashboard</p>
                  <div className="form-field" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <Input
                      type="color"
                      value={settings.reminder_color || '#f59e0b'}
                      onChange={(e) => updateSettings('reminder_color', e.target.value)}
                      style={{ width: '80px', height: '50px', cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#666' }}>
                        Current color: {settings.reminder_color || '#f59e0b'}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateSettings('reminder_color', '#f59e0b')}
                        style={{ marginTop: '0.5rem' }}
                      >
                        Reset to Default (Orange)
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="business-section">
                  <h3>Due Today Colour</h3>
                  <p className="section-description">Choose the color for the due today reminders box on your dashboard</p>
                  <div className="form-field" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <Input
                      type="color"
                      value={settings.due_today_color || '#f59e0b'}
                      onChange={(e) => updateSettings('due_today_color', e.target.value)}
                      style={{ width: '80px', height: '50px', cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#666' }}>
                        Current color: {settings.due_today_color || '#f59e0b'}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateSettings('due_today_color', '#f59e0b')}
                        style={{ marginTop: '0.5rem' }}
                      >
                        Reset to Default (Orange)
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="business-section">
                  <h3>Awaiting Payment Color</h3>
                  <p className="section-description">Choose the color for the awaiting payment box on your dashboard</p>
                  <div className="form-field" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <Input
                      type="color"
                      value={settings.awaiting_payment_color || '#eab308'}
                      onChange={(e) => updateSettings('awaiting_payment_color', e.target.value)}
                      style={{ width: '80px', height: '50px', cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#666' }}>
                        Current color: {settings.awaiting_payment_color || '#eab308'}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateSettings('awaiting_payment_color', '#eab308')}
                        style={{ marginTop: '0.5rem' }}
                      >
                        Reset to Default (Gold)
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="business-section">
                  <h3>Terms and Conditions</h3>
                  <p className="section-description">This will appear at the bottom of printouts and emails</p>
                  <div className="form-field">
                    <Textarea
                      value={settings.terms_and_conditions || ''}
                      onChange={(e) => updateSettings('terms_and_conditions', e.target.value)}
                      rows={6}
                      data-testid="terms-input"
                    />
                  </div>
                </div>
              </div>
              ) : (
                <div className="loading">Loading business information...</div>
              )}
            </TabsContent>

            <TabsContent value="email">
              <div className="email-settings">
                <div className="email-section">
                  <h3>
                    Email Configuration
                    <HelpTooltip 
                      content="Set up SMTP or Brevo (SendinBlue) to send quotes, invoices, and notifications. Brevo is recommended for reliable delivery." 
                      side="right"
                    />
                  </h3>
                  <p className="section-description">Configure SMTP settings to enable email functionality (invoice sending, notifications, etc.)</p>
                  <div className="email-form">
                    <div className="form-field">
                      <Label htmlFor="smtp-email">Email Address</Label>
                      <Input
                        id="smtp-email"
                        type="email"
                        placeholder="your-email@gmail.com"
                        value={settings.email_config?.email || ''}
                        onChange={(e) => {
                          const updatedConfig = {...(settings.email_config || {}), email: e.target.value};
                          updateSettings('email_config', updatedConfig);
                        }}
                      />
                      <p className="input-hint">This email will be used as the sender address for all outgoing emails</p>
                    </div>
                    
                    <div className="form-field">
                      <Label htmlFor="smtp-server">SMTP Server</Label>
                      <Input
                        id="smtp-server"
                        placeholder="smtp.gmail.com"
                        value={settings.email_config?.smtp_server || ''}
                        onChange={(e) => {
                          const updatedConfig = {...(settings.email_config || {}), smtp_server: e.target.value};
                          updateSettings('email_config', updatedConfig);
                        }}
                      />
                      <p className="input-hint">
                        Common SMTP servers:<br/>
                        • Gmail: smtp.gmail.com<br/>
                        • Outlook: smtp-mail.outlook.com<br/>
                        • Yahoo: smtp.mail.yahoo.com
                      </p>
                    </div>
                    
                    <div className="form-field">
                      <Label htmlFor="smtp-port">SMTP Port</Label>
                      <Input
                        id="smtp-port"
                        type="number"
                        placeholder="587"
                        value={settings.email_config?.smtp_port || 587}
                        onChange={(e) => {
                          const updatedConfig = {...(settings.email_config || {}), smtp_port: parseInt(e.target.value) || 587};
                          updateSettings('email_config', updatedConfig);
                        }}
                      />
                      <p className="input-hint">Standard port is 587 (TLS). Other common ports: 465 (SSL), 25 (Non-encrypted)</p>
                    </div>
                    
                    <div className="form-field">
                      <Label htmlFor="smtp-password">SMTP Password / App Password</Label>
                      <Input
                        id="smtp-password"
                        type="password"
                        placeholder={settings.email_config?.smtp_password_set ? '•••••• stored (leave blank to keep, type new to replace)' : 'Enter SMTP / App Password'}
                        value={settings.email_config?.smtp_password || ''}
                        onChange={(e) => {
                          const updatedConfig = {...(settings.email_config || {}), smtp_password: e.target.value};
                          updateSettings('email_config', updatedConfig);
                        }}
                        data-testid="smtp-password-input"
                      />
                      <p className="input-hint">
                        {settings.email_config?.smtp_password_set ? (
                          <span style={{ color: '#059669', fontWeight: 500 }}>
                            ✓ A password is currently stored. Leave this blank to keep it; type a new one to replace it.
                          </span>
                        ) : (
                          <span style={{ color: '#dc2626', fontWeight: 500 }}>
                            ⚠ No password stored. Email Invoice / Quote will fail until you enter one.
                          </span>
                        )}
                        <br/>
                        <strong>⚠️ Recommended:</strong> use a provider "App Password" (not your regular email password) — Microsoft 365 App Passwords (with 2FA enabled) and Gmail App Passwords are revocable independently of your main account.<br/>
                        <a href="https://support.microsoft.com/en-us/account-billing/manage-app-passwords-for-two-step-verification-d6dc8c6d-4bf7-4851-ad95-6d07799387e9" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>
                          Microsoft 365 App Password guide
                        </a>
                        {' · '}
                        <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>
                          Gmail App Password guide
                        </a>
                      </p>
                    </div>

                    <div style={{ 
                      marginTop: '1.5rem', 
                      padding: '1rem', 
                      backgroundColor: '#f0f9ff', 
                      border: '1px solid #bfdbfe',
                      borderRadius: '0.5rem'
                    }}>
                      <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: '#1e40af' }}>
                        ℹ️ Configuration Status
                      </h4>
                      {settings.email_config?.brevo_api_key ? (
                        <p style={{ fontSize: '0.875rem', color: '#166534', margin: 0 }}>
                          ✅ Brevo API is configured — invoice emails will be sent via Brevo (recommended path, no SMTP password needed).
                        </p>
                      ) : (settings.email_config?.email && settings.email_config?.smtp_server && settings.email_config?.smtp_password_set) ? (
                        <p style={{ fontSize: '0.875rem', color: '#166534', margin: 0 }}>
                          ✅ SMTP is configured. Invoice emails will be sent via SMTP. <strong>Recommendation:</strong> add a Brevo API key below for better deliverability and security.
                        </p>
                      ) : (
                        <p style={{ fontSize: '0.875rem', color: '#991b1b', margin: 0 }}>
                          ❌ Email not configured. Invoice / Quote emails will fail. Add a Brevo API key (recommended) OR fill in the SMTP fields.
                        </p>
                      )}
                    </div>

                    {/* Test Connection Button */}
                    <div style={{ marginTop: '1.5rem' }}>
                      <Button 
                        onClick={handleTestEmailConnection}
                        disabled={testingEmailConnection || !settings.email_config?.email || !settings.email_config?.smtp_server}
                        data-testid="test-email-connection-button"
                        variant="outline"
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem'
                        }}
                      >
                        {testingEmailConnection ? (
                          <>
                            <span className="animate-spin">⏳</span>
                            Testing Connection...
                          </>
                        ) : (
                          <>
                            🔌 Test Connection
                          </>
                        )}
                      </Button>
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                        Click to verify your SMTP credentials are working correctly. This will attempt to connect and authenticate with your email server.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Brevo API Section */}
                <div className="email-section" style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
                  <h3>
                    Brevo API (Recommended)
                    <HelpTooltip 
                      content="Brevo (formerly SendinBlue) provides reliable email delivery. Use this instead of SMTP for better deliverability of quotes, invoices, and notifications." 
                      side="right"
                    />
                  </h3>
                  <p className="section-description">
                    For better email deliverability, we recommend using Brevo's API instead of SMTP. 
                    <a href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', marginLeft: '0.25rem' }}>
                      Get your API key from Brevo →
                    </a>
                  </p>
                  <div className="email-form" style={{ marginTop: '1rem' }}>
                    <div className="form-field">
                      <Label htmlFor="brevo-api-key">Brevo API Key</Label>
                      <Input
                        id="brevo-api-key"
                        type="password"
                        placeholder="xkeysib-xxxxxxxxxxxxxxxxxxxxxxxx"
                        value={settings.email_config?.brevo_api_key || ''}
                        onChange={(e) => {
                          const updatedConfig = {...(settings.email_config || {}), brevo_api_key: e.target.value};
                          updateSettings('email_config', updatedConfig);
                        }}
                        data-testid="brevo-api-key-input"
                      />
                      <p className="input-hint">
                        Your Brevo API key starts with "xkeysib-". When set, this will be used for sending emails instead of SMTP.
                      </p>
                    </div>

                    <div style={{ 
                      marginTop: '1rem', 
                      padding: '1rem', 
                      backgroundColor: settings.email_config?.brevo_api_key ? '#f0fdf4' : '#fef3c7', 
                      border: `1px solid ${settings.email_config?.brevo_api_key ? '#86efac' : '#fcd34d'}`,
                      borderRadius: '0.5rem'
                    }}>
                      {settings.email_config?.brevo_api_key ? (
                        <p style={{ fontSize: '0.875rem', color: '#166534', margin: 0 }}>
                          ✅ Brevo API key is configured. Emails will be sent via Brevo.
                        </p>
                      ) : (
                        <p style={{ fontSize: '0.875rem', color: '#92400e', margin: 0 }}>
                          ⚠️ No Brevo API key set. Emails will use SMTP settings above (if configured).
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Email Templates Tab */}
            <TabsContent value="templates">
              <div className="email-templates-settings">
                <h3>Email Templates</h3>
                <p className="section-description">
                  Customize email templates sent to customers. Use placeholders like {'{customer_name}'}, {'{job_number}'}, {'{device_info}'}, etc.
                </p>

                <CustomerEmailTemplatesEditor
                  templates={settings.email_templates?.customer_templates || []}
                  onSave={async (nextTemplates) => {
                    // Persist the full array in one PUT. We deliberately re-send
                    // the *entire* email_templates object so we don't accidentally
                    // wipe sibling templates (invoice/quote/etc.) on the backend.
                    const updatedTemplates = {
                      ...(settings.email_templates || {}),
                      customer_templates: nextTemplates,
                    };
                    setSettings({ ...settings, email_templates: updatedTemplates });
                    await axios.put(`${API}/settings`, { email_templates: updatedTemplates });
                  }}
                />
                
                <div className="template-section" style={{ marginTop: '1.5rem' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: '1rem' }}>Invoice Email</CardTitle>
                      <CardDescription>
                        Default subject + message used when emailing an invoice to a customer. You'll be able to edit it for each customer at send time. Placeholders: {'{customer_name}'}, {'{job_number}'}, {'{total_amount}'}, {'{business_name}'}, {'{business_phone}'}, {'{business_email}'}.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor="invoice-email-subject">Email Subject</Label>
                        <Input
                          id="invoice-email-subject"
                          value={settings.email_templates?.invoice_email?.subject || 'Invoice #{job_number} from {business_name}'}
                          onChange={(e) => {
                            const updatedTemplates = {
                              ...settings.email_templates,
                              invoice_email: {
                                ...settings.email_templates?.invoice_email,
                                subject: e.target.value
                              }
                            };
                            setSettings({ ...settings, email_templates: updatedTemplates });
                          }}
                          data-testid="invoice-email-template-subject"
                        />
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor="invoice-email-body">Email Body</Label>
                        <Textarea
                          id="invoice-email-body"
                          rows={14}
                          value={settings.email_templates?.invoice_email?.body || ''}
                          onChange={(e) => {
                            const updatedTemplates = {
                              ...settings.email_templates,
                              invoice_email: {
                                ...settings.email_templates?.invoice_email,
                                body: e.target.value
                              }
                            };
                            setSettings({ ...settings, email_templates: updatedTemplates });
                          }}
                          data-testid="invoice-email-template-body"
                          style={{ fontFamily: 'inherit', fontSize: '0.875rem' }}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                        <div>
                          <Label htmlFor="invoice-email-default-cc">Default CC (comma-separated)</Label>
                          <Input
                            id="invoice-email-default-cc"
                            placeholder="me@mybusiness.com.au"
                            value={settings.email_templates?.invoice_email?.default_cc || ''}
                            onChange={(e) => {
                              const updatedTemplates = {
                                ...settings.email_templates,
                                invoice_email: {
                                  ...settings.email_templates?.invoice_email,
                                  default_cc: e.target.value
                                }
                              };
                              setSettings({ ...settings, email_templates: updatedTemplates });
                            }}
                            data-testid="invoice-email-default-cc"
                          />
                          <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                            Auto-fills the CC field on every invoice send. Great for self-archiving.
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="invoice-email-default-bcc">Default BCC (comma-separated)</Label>
                          <Input
                            id="invoice-email-default-bcc"
                            placeholder="accountant@example.com"
                            value={settings.email_templates?.invoice_email?.default_bcc || ''}
                            onChange={(e) => {
                              const updatedTemplates = {
                                ...settings.email_templates,
                                invoice_email: {
                                  ...settings.email_templates?.invoice_email,
                                  default_bcc: e.target.value
                                }
                              };
                              setSettings({ ...settings, email_templates: updatedTemplates });
                            }}
                            data-testid="invoice-email-default-bcc"
                          />
                          <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                            Hidden recipient on every invoice send. The customer can't see this.
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/settings`, { email_templates: settings.email_templates });
                            toast.success('Invoice email template saved');
                          } catch (err) {
                            toast.error('Failed to save template');
                          }
                        }}
                        data-testid="save-invoice-email-template"
                        style={{ backgroundColor: '#2563eb', color: 'white' }}
                      >
                        Save Invoice Email Template
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Feb 2026 — Paid Tax Invoice template. Same shape as
                      invoice_email; auto-selected by /email-invoice when
                      job.payment_status === 'Paid'. Operator can override
                      per send via the template dropdown on the Email
                      Invoice dialog. */}
                  <Card style={{ marginTop: '1.5rem' }} data-testid="paid-invoice-email-template-card">
                    <CardHeader>
                      <CardTitle style={{ fontSize: '1rem' }}>Paid Tax Invoice Email</CardTitle>
                      <CardDescription>
                        Auto-selected when the job's payment status is <b>Paid</b>. Same placeholders as Invoice Email.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label>Email Subject</Label>
                        <Input
                          value={settings.email_templates?.paid_invoice_email?.subject || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            email_templates: {
                              ...settings.email_templates,
                              paid_invoice_email: { ...settings.email_templates?.paid_invoice_email, subject: e.target.value }
                            }
                          })}
                          data-testid="paid-invoice-email-template-subject"
                        />
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label>Email Body</Label>
                        <Textarea
                          rows={12}
                          value={settings.email_templates?.paid_invoice_email?.body || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            email_templates: {
                              ...settings.email_templates,
                              paid_invoice_email: { ...settings.email_templates?.paid_invoice_email, body: e.target.value }
                            }
                          })}
                          data-testid="paid-invoice-email-template-body"
                          style={{ fontFamily: 'inherit', fontSize: '0.875rem' }}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                        <div>
                          <Label>Default CC</Label>
                          <Input
                            value={settings.email_templates?.paid_invoice_email?.default_cc || ''}
                            onChange={(e) => setSettings({
                              ...settings,
                              email_templates: {
                                ...settings.email_templates,
                                paid_invoice_email: { ...settings.email_templates?.paid_invoice_email, default_cc: e.target.value }
                              }
                            })}
                          />
                        </div>
                        <div>
                          <Label>Default BCC</Label>
                          <Input
                            value={settings.email_templates?.paid_invoice_email?.default_bcc || ''}
                            onChange={(e) => setSettings({
                              ...settings,
                              email_templates: {
                                ...settings.email_templates,
                                paid_invoice_email: { ...settings.email_templates?.paid_invoice_email, default_bcc: e.target.value }
                              }
                            })}
                          />
                        </div>
                      </div>
                      <Button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/settings`, { email_templates: settings.email_templates });
                            toast.success('Paid Tax Invoice template saved');
                          } catch { toast.error('Failed to save template'); }
                        }}
                        data-testid="save-paid-invoice-email-template"
                        style={{ backgroundColor: '#16a34a', color: 'white' }}
                      >
                        Save Paid Tax Invoice Template
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Feb 2026 — Google review request template. Uses the
                      review URL configured just above. */}
                  <Card style={{ marginTop: '1.5rem' }} data-testid="review-request-template-card">
                    <CardHeader>
                      <CardTitle style={{ fontSize: '1rem' }}>⭐ Google Review Request Email</CardTitle>
                      <CardDescription>
                        Sent from Job Detail or Reports → Review Requests. Additional placeholder: {'{review_link}'} — pulls from the Google Review URL below.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label>Google Review URL</Label>
                        <Input
                          placeholder="https://g.page/r/..."
                          value={settings.google_review_url || ''}
                          onChange={(e) => setSettings({ ...settings, google_review_url: e.target.value })}
                          data-testid="google-review-url"
                        />
                        <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                          One-off setup. Grab your link from Google Business Profile → "Ask for reviews".
                        </p>
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label>Email Subject</Label>
                        <Input
                          value={settings.email_templates?.review_request?.subject || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            email_templates: {
                              ...settings.email_templates,
                              review_request: { ...settings.email_templates?.review_request, subject: e.target.value }
                            }
                          })}
                          data-testid="review-request-template-subject"
                        />
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label>Email Body</Label>
                        <Textarea
                          rows={12}
                          value={settings.email_templates?.review_request?.body || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            email_templates: {
                              ...settings.email_templates,
                              review_request: { ...settings.email_templates?.review_request, body: e.target.value }
                            }
                          })}
                          data-testid="review-request-template-body"
                          style={{ fontFamily: 'inherit', fontSize: '0.875rem' }}
                        />
                      </div>
                      <Button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/settings`, {
                              email_templates: settings.email_templates,
                              google_review_url: settings.google_review_url || ''
                            });
                            toast.success('Review Request template saved');
                          } catch { toast.error('Failed to save template'); }
                        }}
                        data-testid="save-review-request-template"
                        style={{ backgroundColor: '#7c3aed', color: 'white' }}
                      >
                        Save Review Request Template
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Feb 2026 — PayPal payment link + {paypal_button}
                      placeholder. Stored once here, reused in any customer
                      email template that includes `{paypal_button}` or
                      `{paypal_link}`. Toggle lets the operator hide it
                      without clearing the URL. */}
                  <Card style={{ marginTop: '1.5rem' }} data-testid="paypal-button-settings-card">
                    <CardHeader>
                      <CardTitle style={{ fontSize: '1rem' }}>💳 PayPal "Pay with PayPal" Button</CardTitle>
                      <CardDescription>
                        Store your PayPal payment link once — then drop <code>{'{paypal_button}'}</code> into any email template body and a branded button appears in that email. Use <code>{'{paypal_link}'}</code> for the raw URL.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '6px', marginBottom: '1rem' }}>
                        <Switch
                          checked={!!settings.paypal_button_enabled}
                          onCheckedChange={async (v) => {
                            // Feb 2026 — auto-save on flip so the operator
                            // doesn't need to remember to click Save after
                            // toggling. The link input still requires an
                            // explicit Save (text input UX).
                            setSettings({ ...settings, paypal_button_enabled: v });
                            try {
                              await axios.put(`${API}/settings`, { paypal_button_enabled: v });
                              toast.success(v ? 'PayPal button is now SHOWING in emails' : 'PayPal button is now HIDDEN in emails');
                            } catch {
                              // Roll back optimistic UI update on failure.
                              setSettings({ ...settings, paypal_button_enabled: !v });
                              toast.error('Failed to save toggle — please try again');
                            }
                          }}
                          data-testid="paypal-button-enabled-toggle"
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                            {settings.paypal_button_enabled ? 'Button is SHOWING in emails' : 'Button is HIDDEN in emails'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            Auto-saves on flip. No need to click Save for the toggle.
                          </div>
                        </div>
                      </div>

                      <div style={{ marginBottom: '1rem' }}>
                        <Label>PayPal Payment Link</Label>
                        <Input
                          placeholder="https://www.paypal.com/paypalme/..."
                          value={settings.paypal_payment_link || ''}
                          onChange={(e) => setSettings({ ...settings, paypal_payment_link: e.target.value })}
                          data-testid="paypal-payment-link-input"
                        />
                        <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                          Paste the PayPal.me / PayPal link your customers use to pay. Pay-in-4 availability depends on your PayPal account & the customer's country.
                        </p>
                      </div>

                      {/* Live preview — mirrors the exact HTML sent in emails */}
                      {!!(settings.paypal_button_enabled && (settings.paypal_payment_link || '').trim()) && (
                        <div style={{ marginBottom: '1rem' }}>
                          <Label>Preview</Label>
                          <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1rem' }} data-testid="paypal-button-preview">
                            <table role="presentation" cellPadding={0} cellSpacing={0} style={{ margin: '0 auto' }}>
                              <tbody><tr><td align="center" style={{ backgroundColor: '#FFC439', borderRadius: '8px' }}>
                                <a href={settings.paypal_payment_link} target="_blank" rel="noreferrer"
                                   style={{ display: 'inline-block', padding: '14px 32px', fontSize: '16px', fontWeight: 700, color: '#003087', textDecoration: 'none', lineHeight: 1.1, minWidth: '180px', textAlign: 'center' }}>
                                  Pay with <span style={{ color: '#003087', fontWeight: 800 }}>Pay</span>
                                  <span style={{ color: '#009cde', fontWeight: 800 }}>Pal</span>
                                  <br/><span style={{ fontSize: '11px', fontWeight: 500, color: '#003087', letterSpacing: '0.3px' }}>Pay in 4 available</span>
                                </a>
                              </td></tr></tbody>
                            </table>
                            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#64748b', textAlign: 'center' }}>
                              Button not showing? <a href={settings.paypal_payment_link} target="_blank" rel="noreferrer" style={{ color: '#0070ba' }}>Click here to pay with PayPal</a>
                            </p>
                          </div>
                        </div>
                      )}

                      <Button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/settings`, {
                              paypal_payment_link: (settings.paypal_payment_link || '').trim(),
                              paypal_button_enabled: !!settings.paypal_button_enabled,
                            });
                            toast.success('PayPal button settings saved');
                          } catch { toast.error('Failed to save PayPal settings'); }
                        }}
                        data-testid="save-paypal-button-settings"
                        style={{ backgroundColor: '#0070ba', color: 'white' }}
                      >
                        Save PayPal Settings
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                <div className="template-section" style={{ marginTop: '1.5rem' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: '1rem' }}>Quote Email</CardTitle>
                      <CardDescription>
                        Default personal message + CC/BCC for quote emails. The personal message pre-fills the dialog when you click "Send Quote", so you don't retype it every time. Placeholders: {'{customer_name}'}, {'{job_number}'}, {'{business_name}'}, {'{business_phone}'}.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <Label htmlFor="quote-email-default-body">Default Personal Message</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            data-testid="insert-quote-template-button"
                            onClick={() => {
                              const sample = `Hi {customer_name},\n\nAs discussed, please find your quote summary for Job #{job_number} below.\n\nLet me know which option you'd like to proceed with, or call us on {business_phone} if you have any questions.\n\nKind regards,\n{business_name}`;
                              const updatedTemplates = {
                                ...settings.email_templates,
                                quote_email: { ...settings.email_templates?.quote_email, body: sample }
                              };
                              setSettings({ ...settings, email_templates: updatedTemplates });
                            }}
                            style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                          >
                            📝 Insert Sample Template
                          </Button>
                        </div>
                        <Textarea
                          id="quote-email-default-body"
                          rows={5}
                          placeholder="(Empty — leave blank to skip the personal-message box, or click 'Insert Sample Template' above for a starting point.)"
                          value={settings.email_templates?.quote_email?.body || ''}
                          onChange={(e) => {
                            const updatedTemplates = {
                              ...settings.email_templates,
                              quote_email: { ...settings.email_templates?.quote_email, body: e.target.value }
                            };
                            setSettings({ ...settings, email_templates: updatedTemplates });
                          }}
                          data-testid="quote-email-default-body"
                          style={{ fontFamily: 'inherit', fontSize: '0.875rem' }}
                        />
                        <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                          Leave blank if you'd prefer the standard quote template only (no personal message box).
                        </p>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                        <div>
                          <Label htmlFor="quote-email-default-cc">Default CC</Label>
                          <Input
                            id="quote-email-default-cc"
                            placeholder="me@mybusiness.com.au"
                            value={settings.email_templates?.quote_email?.default_cc || ''}
                            onChange={(e) => {
                              const updatedTemplates = {
                                ...settings.email_templates,
                                quote_email: { ...settings.email_templates?.quote_email, default_cc: e.target.value }
                              };
                              setSettings({ ...settings, email_templates: updatedTemplates });
                            }}
                            data-testid="quote-email-default-cc"
                          />
                        </div>
                        <div>
                          <Label htmlFor="quote-email-default-bcc">Default BCC</Label>
                          <Input
                            id="quote-email-default-bcc"
                            placeholder="archive@example.com"
                            value={settings.email_templates?.quote_email?.default_bcc || ''}
                            onChange={(e) => {
                              const updatedTemplates = {
                                ...settings.email_templates,
                                quote_email: { ...settings.email_templates?.quote_email, default_bcc: e.target.value }
                              };
                              setSettings({ ...settings, email_templates: updatedTemplates });
                            }}
                            data-testid="quote-email-default-bcc"
                          />
                        </div>
                      </div>
                      <Button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/settings`, { email_templates: settings.email_templates });
                            toast.success('Quote email defaults saved');
                          } catch (err) {
                            toast.error('Failed to save');
                          }
                        }}
                        data-testid="save-quote-email-template"
                        style={{ backgroundColor: '#2563eb', color: 'white' }}
                      >
                        Save Quote Email Defaults
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                <div className="template-section" style={{ marginTop: '1.5rem' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: '1rem' }}>Custom Quote Templates</CardTitle>
                      <CardDescription>
                        Alternate "personal message" bodies you can pick from when you click Send Quote on a job (e.g. Head Swap Quote). The standard quote card (job details, approval button, footer) is always used — these only replace the message text at the top. Use <code>{'{deposit}'}</code> in the body if you want an editable deposit dollar amount per send.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {(settings.email_templates?.quote_custom_templates || []).map((tpl, idx) => (
                        <div key={tpl.id || idx} style={{ border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1rem', marginBottom: '0.75rem', backgroundColor: '#fafafa' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '0.75rem', marginBottom: '0.75rem' }}>
                            <div>
                              <Label>Template Name</Label>
                              <Input
                                value={tpl.name || ''}
                                onChange={(e) => {
                                  const next = [...(settings.email_templates?.quote_custom_templates || [])];
                                  next[idx] = { ...next[idx], name: e.target.value };
                                  setSettings({
                                    ...settings,
                                    email_templates: { ...settings.email_templates, quote_custom_templates: next },
                                  });
                                }}
                                data-testid={`quote-custom-tpl-name-${idx}`}
                              />
                            </div>
                            <div>
                              <Label>Default Deposit ($) <span style={{ color: '#94a3b8', fontWeight: 400 }}>optional</span></Label>
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={tpl.deposit_default ?? ''}
                                placeholder="e.g. 350"
                                onChange={(e) => {
                                  const next = [...(settings.email_templates?.quote_custom_templates || [])];
                                  const raw = e.target.value;
                                  next[idx] = { ...next[idx], deposit_default: raw === '' ? null : parseFloat(raw) };
                                  setSettings({
                                    ...settings,
                                    email_templates: { ...settings.email_templates, quote_custom_templates: next },
                                  });
                                }}
                                data-testid={`quote-custom-tpl-deposit-${idx}`}
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  if (!window.confirm(`Delete the "${tpl.name || 'unnamed'}" template? This cannot be undone.`)) return;
                                  const next = (settings.email_templates?.quote_custom_templates || []).filter((_, i) => i !== idx);
                                  setSettings({
                                    ...settings,
                                    email_templates: { ...settings.email_templates, quote_custom_templates: next },
                                  });
                                }}
                                style={{ color: '#dc2626', borderColor: '#fecaca' }}
                                data-testid={`quote-custom-tpl-delete-${idx}`}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                          <div>
                            <Label>Body</Label>
                            <Textarea
                              value={tpl.body || ''}
                              rows={10}
                              onChange={(e) => {
                                const next = [...(settings.email_templates?.quote_custom_templates || [])];
                                next[idx] = { ...next[idx], body: e.target.value };
                                setSettings({
                                  ...settings,
                                  email_templates: { ...settings.email_templates, quote_custom_templates: next },
                                });
                              }}
                              style={{ fontFamily: 'inherit', fontSize: '0.875rem', minHeight: '160px' }}
                              data-testid={`quote-custom-tpl-body-${idx}`}
                            />
                          </div>
                          {/* Feb 2026 — per-template Default CC / BCC. Applied
                              when the operator switches to this template on
                              the Send Quote dialog; user can still override
                              per-send. */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
                            <div>
                              <Label>Default CC (comma-separated)</Label>
                              <Input
                                placeholder="me@mybusiness.com.au"
                                value={tpl.default_cc || ''}
                                onChange={(e) => {
                                  const next = [...(settings.email_templates?.quote_custom_templates || [])];
                                  next[idx] = { ...next[idx], default_cc: e.target.value };
                                  setSettings({
                                    ...settings,
                                    email_templates: { ...settings.email_templates, quote_custom_templates: next },
                                  });
                                }}
                                data-testid={`quote-custom-tpl-cc-${idx}`}
                              />
                              <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                                Auto-fills the CC field when this template is selected.
                              </p>
                            </div>
                            <div>
                              <Label>Default BCC (comma-separated)</Label>
                              <Input
                                placeholder="archive@example.com"
                                value={tpl.default_bcc || ''}
                                onChange={(e) => {
                                  const next = [...(settings.email_templates?.quote_custom_templates || [])];
                                  next[idx] = { ...next[idx], default_bcc: e.target.value };
                                  setSettings({
                                    ...settings,
                                    email_templates: { ...settings.email_templates, quote_custom_templates: next },
                                  });
                                }}
                                data-testid={`quote-custom-tpl-bcc-${idx}`}
                              />
                              <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                                Hidden recipient — customer can&apos;t see this.
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}

                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            const existing = settings.email_templates?.quote_custom_templates || [];
                            const newTpl = {
                              id: `tpl_${Date.now()}`,
                              name: 'New Template',
                              body: '',
                              deposit_default: null,
                            };
                            setSettings({
                              ...settings,
                              email_templates: {
                                ...settings.email_templates,
                                quote_custom_templates: [...existing, newTpl],
                              },
                            });
                          }}
                          data-testid="add-quote-custom-tpl"
                        >
                          + Add Template
                        </Button>
                        <Button
                          type="button"
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/settings`, { email_templates: settings.email_templates });
                              toast.success('Custom quote templates saved');
                            } catch (err) {
                              toast.error('Failed to save custom quote templates');
                            }
                          }}
                          style={{ backgroundColor: '#2563eb', color: 'white' }}
                          data-testid="save-quote-custom-tpls"
                        >
                          Save Custom Templates
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="template-section" style={{ marginTop: '1.5rem' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: '1rem' }}>Quote Rejected - Collection Notice</CardTitle>
                      <CardDescription>
                        This email is automatically sent when a customer rejects a quote. It notifies them to collect their item within 7 days.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor="collection-subject">Email Subject</Label>
                        <Input
                          id="collection-subject"
                          value={settings.email_templates?.quote_rejected_collection?.subject || 'Job #{job_number} - Please Collect Your Item'}
                          onChange={(e) => {
                            const updatedTemplates = {
                              ...settings.email_templates,
                              quote_rejected_collection: {
                                ...settings.email_templates?.quote_rejected_collection,
                                subject: e.target.value
                              }
                            };
                            setSettings({ ...settings, email_templates: updatedTemplates });
                          }}
                          data-testid="collection-email-subject"
                        />
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor="collection-body">Email Body</Label>
                        <Textarea
                          id="collection-body"
                          rows={15}
                          value={settings.email_templates?.quote_rejected_collection?.body || `Dear {customer_name},

Thank you for considering our services. We understand that you have decided not to proceed with the quoted work for your job.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
JOB DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Job Number: {job_number}
Device: {device_info}
Date Received: {date_in}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
COLLECTION NOTICE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Please collect your item within 7 days from the date of this email.

Our business hours are:
Monday - Friday: 9:00 AM - 5:00 PM

If you have any questions or need to arrange an alternative collection time, please don't hesitate to contact us.

Best regards,
{business_name}
{business_phone}
{business_email}`}
                          onChange={(e) => {
                            const updatedTemplates = {
                              ...settings.email_templates,
                              quote_rejected_collection: {
                                ...settings.email_templates?.quote_rejected_collection,
                                body: e.target.value
                              }
                            };
                            setSettings({ ...settings, email_templates: updatedTemplates });
                          }}
                          data-testid="collection-email-body"
                        />
                      </div>
                      {/* Feb 2026 — Default CC / BCC. Auto-applied to every
                          collection-notice email fired when a customer rejects
                          the quote (and to the first-notice scheduler run). */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                        <div>
                          <Label htmlFor="collection-default-cc">Default CC (comma-separated)</Label>
                          <Input
                            id="collection-default-cc"
                            placeholder="me@mybusiness.com.au"
                            value={settings.email_templates?.quote_rejected_collection?.default_cc || ''}
                            onChange={(e) => {
                              setSettings({
                                ...settings,
                                email_templates: {
                                  ...settings.email_templates,
                                  quote_rejected_collection: {
                                    ...settings.email_templates?.quote_rejected_collection,
                                    default_cc: e.target.value,
                                  },
                                },
                              });
                            }}
                            data-testid="collection-default-cc"
                          />
                          <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                            Auto-CC on every collection notice send.
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="collection-default-bcc">Default BCC (comma-separated)</Label>
                          <Input
                            id="collection-default-bcc"
                            placeholder="archive@example.com"
                            value={settings.email_templates?.quote_rejected_collection?.default_bcc || ''}
                            onChange={(e) => {
                              setSettings({
                                ...settings,
                                email_templates: {
                                  ...settings.email_templates,
                                  quote_rejected_collection: {
                                    ...settings.email_templates?.quote_rejected_collection,
                                    default_bcc: e.target.value,
                                  },
                                },
                              });
                            }}
                            data-testid="collection-default-bcc"
                          />
                          <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                            Hidden recipient — customer can&apos;t see this.
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <Button
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/settings`, { email_templates: settings.email_templates });
                              toast.success('Email template saved successfully!');
                            } catch (error) {
                              toast.error('Failed to save template');
                            }
                          }}
                          data-testid="save-template-button"
                        >
                          Save Template
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            const defaultTemplate = {
                              subject: 'Job #{job_number} - Please Collect Your Item',
                              body: `Dear {customer_name},

Thank you for considering our services. We understand that you have decided not to proceed with the quoted work for your job.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
JOB DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Job Number: {job_number}
Device: {device_info}
Date Received: {date_in}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
COLLECTION NOTICE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Please collect your item within 7 days from the date of this email.

Our business hours are:
Monday - Friday: 9:00 AM - 5:00 PM

If you have any questions or need to arrange an alternative collection time, please don't hesitate to contact us.

Best regards,
{business_name}
{business_phone}
{business_email}`
                            };
                            setSettings({
                              ...settings,
                              email_templates: {
                                ...settings.email_templates,
                                quote_rejected_collection: defaultTemplate
                              }
                            });
                            toast.info('Template reset to default (not saved yet)');
                          }}
                          data-testid="reset-template-button"
                        >
                          Reset to Default
                        </Button>
                      </div>
                      <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f3f4f6', borderRadius: '0.375rem' }}>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                          <strong>Available Placeholders:</strong>
                        </p>
                        <code style={{ fontSize: '0.75rem', color: '#4b5563' }}>
                          {'{customer_name}'} • {'{job_number}'} • {'{device_info}'} • {'{date_in}'} • {'{business_name}'} • {'{business_phone}'} • {'{business_email}'}
                        </code>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Second Warning / Final Notice Template */}
                <div className="template-section" style={{ marginTop: '1.5rem' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: '1rem', color: '#dc2626' }}>⚠️ 2nd Warning / Final Notice</CardTitle>
                      <CardDescription>
                        This email is automatically sent 7 days after the first collection notice if the item remains uncollected. 
                        It warns the customer that their item may be disposed of.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor="second-warning-subject">Email Subject</Label>
                        <Input
                          id="second-warning-subject"
                          value={settings.email_templates?.second_warning_collection?.subject || 'FINAL NOTICE - Job #{job_number} - Urgent Collection Required'}
                          onChange={(e) => {
                            const updatedTemplates = {
                              ...settings.email_templates,
                              second_warning_collection: {
                                ...settings.email_templates?.second_warning_collection,
                                subject: e.target.value
                              }
                            };
                            setSettings({ ...settings, email_templates: updatedTemplates });
                          }}
                          data-testid="second-warning-subject"
                        />
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor="second-warning-body">Email Body</Label>
                        <Textarea
                          id="second-warning-body"
                          rows={18}
                          value={settings.email_templates?.second_warning_collection?.body || `Dear {customer_name},

This is a FINAL NOTICE regarding your uncollected item.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
JOB DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Job Number: {job_number}
Device: {device_info}
Date Received: {date_in}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚠️ URGENT - FINAL WARNING
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

We previously notified you to collect your item, but it remains uncollected.

You have 7 DAYS from the date of this email to collect your item. After this period, your item may be disposed of in accordance with our terms and conditions.

Our business hours are:
Monday - Friday: 9:00 AM - 5:00 PM

Please contact us immediately to arrange collection.

Best regards,
{business_name}
{business_phone}
{business_email}`}
                          onChange={(e) => {
                            const updatedTemplates = {
                              ...settings.email_templates,
                              second_warning_collection: {
                                ...settings.email_templates?.second_warning_collection,
                                body: e.target.value
                              }
                            };
                            setSettings({ ...settings, email_templates: updatedTemplates });
                          }}
                          data-testid="second-warning-body"
                        />
                      </div>
                      {/* Feb 2026 — Default CC / BCC. Auto-applied to every
                          final-notice email fired by the collection-warning
                          scheduler (or manual re-send). */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                        <div>
                          <Label htmlFor="second-warning-default-cc">Default CC (comma-separated)</Label>
                          <Input
                            id="second-warning-default-cc"
                            placeholder="me@mybusiness.com.au"
                            value={settings.email_templates?.second_warning_collection?.default_cc || ''}
                            onChange={(e) => {
                              setSettings({
                                ...settings,
                                email_templates: {
                                  ...settings.email_templates,
                                  second_warning_collection: {
                                    ...settings.email_templates?.second_warning_collection,
                                    default_cc: e.target.value,
                                  },
                                },
                              });
                            }}
                            data-testid="second-warning-default-cc"
                          />
                          <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                            Auto-CC on every final notice send.
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="second-warning-default-bcc">Default BCC (comma-separated)</Label>
                          <Input
                            id="second-warning-default-bcc"
                            placeholder="archive@example.com"
                            value={settings.email_templates?.second_warning_collection?.default_bcc || ''}
                            onChange={(e) => {
                              setSettings({
                                ...settings,
                                email_templates: {
                                  ...settings.email_templates,
                                  second_warning_collection: {
                                    ...settings.email_templates?.second_warning_collection,
                                    default_bcc: e.target.value,
                                  },
                                },
                              });
                            }}
                            data-testid="second-warning-default-bcc"
                          />
                          <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                            Hidden recipient — customer can&apos;t see this.
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <Button
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/settings`, { email_templates: settings.email_templates });
                              toast.success('Second warning template saved successfully!');
                            } catch (error) {
                              toast.error('Failed to save template');
                            }
                          }}
                          data-testid="save-second-warning-button"
                        >
                          Save Template
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            const defaultTemplate = {
                              subject: 'FINAL NOTICE - Job #{job_number} - Urgent Collection Required',
                              body: `Dear {customer_name},

This is a FINAL NOTICE regarding your uncollected item.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
JOB DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Job Number: {job_number}
Device: {device_info}
Date Received: {date_in}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚠️ URGENT - FINAL WARNING
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

We previously notified you to collect your item, but it remains uncollected.

You have 7 DAYS from the date of this email to collect your item. After this period, your item may be disposed of in accordance with our terms and conditions.

Our business hours are:
Monday - Friday: 9:00 AM - 5:00 PM

Please contact us immediately to arrange collection.

Best regards,
{business_name}
{business_phone}
{business_email}`
                            };
                            setSettings({
                              ...settings,
                              email_templates: {
                                ...settings.email_templates,
                                second_warning_collection: defaultTemplate
                              }
                            });
                            toast.info('Template reset to default (not saved yet)');
                          }}
                          data-testid="reset-second-warning-button"
                        >
                          Reset to Default
                        </Button>
                      </div>
                      <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fef2f2', borderRadius: '0.375rem', border: '1px solid #fecaca' }}>
                        <p style={{ fontSize: '0.75rem', color: '#991b1b', marginBottom: '0.5rem' }}>
                          <strong>📧 Automatic Sending:</strong> This email is sent automatically by the scheduler (see settings below).
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                          <strong>Available Placeholders:</strong>
                        </p>
                        <code style={{ fontSize: '0.75rem', color: '#4b5563' }}>
                          {'{customer_name}'} • {'{job_number}'} • {'{device_info}'} • {'{date_in}'} • {'{business_name}'} • {'{business_phone}'} • {'{business_email}'}
                        </code>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Collection Warning Scheduler Settings */}
                <div className="template-section" style={{ marginTop: '1.5rem' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: '1rem' }}>⏰ Collection Warning Scheduler</CardTitle>
                      <CardDescription>
                        Configure when the system checks for jobs that need a second warning email.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div className="form-field">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <input
                              type="checkbox"
                              id="collection-warning-enabled"
                              checked={settings.collection_warning_enabled !== false}
                              onChange={(e) => updateSettings('collection_warning_enabled', e.target.checked)}
                              style={{ width: '1.25rem', height: '1.25rem' }}
                            />
                            <Label htmlFor="collection-warning-enabled" style={{ marginBottom: 0 }}>
                              Enable automatic second warning emails
                            </Label>
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                          <div className="form-field" style={{ flex: '1 1 200px' }}>
                            <Label htmlFor="collection-warning-time">Daily Check Time</Label>
                            <Input
                              id="collection-warning-time"
                              type="time"
                              value={settings.collection_warning_time || '14:00'}
                              onChange={(e) => updateSettings('collection_warning_time', e.target.value)}
                              disabled={settings.collection_warning_enabled === false}
                              data-testid="collection-warning-time"
                            />
                            <p className="input-hint">Time when the system checks for jobs needing second warnings</p>
                          </div>
                          
                          <div className="form-field" style={{ flex: '1 1 200px' }}>
                            <Label htmlFor="collection-warning-days">Days Before Second Warning</Label>
                            <Input
                              id="collection-warning-days"
                              type="number"
                              min="1"
                              max="30"
                              value={settings.collection_warning_days || 7}
                              onChange={(e) => updateSettings('collection_warning_days', parseInt(e.target.value) || 7)}
                              disabled={settings.collection_warning_enabled === false}
                              data-testid="collection-warning-days"
                            />
                            <p className="input-hint">Number of days after first notice before sending second warning</p>
                          </div>
                        </div>

                        {/* Auto-Reminder Settings */}
                        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginTop: '0.5rem' }}>
                          <div className="form-field">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              <input
                                type="checkbox"
                                id="auto-reminder-enabled"
                                checked={settings.auto_reminder_after_second_warning !== false}
                                onChange={(e) => updateSettings('auto_reminder_after_second_warning', e.target.checked)}
                                disabled={settings.collection_warning_enabled === false}
                                style={{ width: '1.25rem', height: '1.25rem' }}
                              />
                              <Label htmlFor="auto-reminder-enabled" style={{ marginBottom: 0 }}>
                                📞 Create follow-up reminder after second warning
                              </Label>
                            </div>
                            <p className="input-hint" style={{ marginLeft: '1.75rem', marginTop: 0 }}>
                              Automatically creates a reminder to call the customer after the 2nd warning email is sent
                            </p>
                          </div>
                          
                          <div className="form-field" style={{ maxWidth: '200px', marginTop: '0.75rem' }}>
                            <Label htmlFor="auto-reminder-days">Days After Second Warning</Label>
                            <Input
                              id="auto-reminder-days"
                              type="number"
                              min="1"
                              max="30"
                              value={settings.auto_reminder_days_after_second_warning || 6}
                              onChange={(e) => updateSettings('auto_reminder_days_after_second_warning', parseInt(e.target.value) || 6)}
                              disabled={settings.collection_warning_enabled === false || settings.auto_reminder_after_second_warning === false}
                              data-testid="auto-reminder-days"
                            />
                            <p className="input-hint">Days to wait before creating the follow-up reminder</p>
                          </div>
                        </div>
                        
                        <div style={{ padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '0.5rem', border: '1px solid #bfdbfe' }}>
                          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: '#1e40af' }}>
                            ℹ️ How it works
                          </h4>
                          <ol style={{ fontSize: '0.875rem', color: '#374151', margin: 0, paddingLeft: '1.25rem' }}>
                            <li style={{ marginBottom: '0.25rem' }}>When a job status changes to "Not Proceeding", the first collection notice is sent immediately</li>
                            <li style={{ marginBottom: '0.25rem' }}>After {settings.collection_warning_days || 7} days, the second/final warning email is sent</li>
                            {settings.auto_reminder_after_second_warning !== false && (
                              <li style={{ marginBottom: '0.25rem', color: '#059669', fontWeight: '500' }}>
                                📞 {settings.auto_reminder_days_after_second_warning || 6} days after the 2nd warning, a reminder is created to call the customer
                              </li>
                            )}
                            <li>Reminders appear on Dashboard and Reminders page</li>
                          </ol>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* iPad Kiosk Settings */}
                <div className="template-section" style={{ marginTop: '1.5rem' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: '1rem' }}>📱 iPad Kiosk Settings</CardTitle>
                      <CardDescription>
                        Configure the customer-facing iPad signature terminal
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                        <input
                          type="checkbox"
                          id="kiosk-auto-email"
                          checked={settings.kiosk_auto_email !== false}
                          onChange={(e) => updateSettings('kiosk_auto_email', e.target.checked)}
                          style={{ width: '1.25rem', height: '1.25rem' }}
                        />
                        <Label htmlFor="kiosk-auto-email" style={{ marginBottom: 0 }}>
                          Auto-send job card email after customer signs
                        </Label>
                      </div>
                      <p className="input-hint" style={{ marginBottom: '1rem', marginTop: '-0.5rem', marginLeft: '1.75rem' }}>
                        When enabled, customers receive an email with their signed job card immediately after signing on the iPad
                      </p>
                      
                      <div className="form-field" style={{ maxWidth: '200px' }}>
                        <Label htmlFor="kiosk-thankyou-duration">Thank You Screen Duration (seconds)</Label>
                        <Input
                          id="kiosk-thankyou-duration"
                          type="number"
                          min="3"
                          max="15"
                          value={settings.kiosk_thank_you_duration || 5}
                          onChange={(e) => updateSettings('kiosk_thank_you_duration', parseInt(e.target.value) || 5)}
                          data-testid="kiosk-thankyou-duration"
                        />
                        <p className="input-hint">How long to show the "Thank You" screen before returning to waiting</p>
                      </div>
                      
                      <div style={{ padding: '1rem', backgroundColor: '#f0fdf4', borderRadius: '0.5rem', border: '1px solid #bbf7d0', marginTop: '1rem' }}>
                        <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: '#166534' }}>
                          📱 iPad Setup Instructions
                        </h4>
                        <ol style={{ fontSize: '0.875rem', color: '#374151', margin: 0, paddingLeft: '1.25rem' }}>
                          <li style={{ marginBottom: '0.25rem' }}>On your iPad, open Safari and go to: <code style={{ background: '#e5e7eb', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>{window.location.origin}/kiosk</code></li>
                          <li style={{ marginBottom: '0.25rem' }}>Tap Share → "Add to Home Screen" to create an app icon</li>
                          <li style={{ marginBottom: '0.25rem' }}>Enable Guided Access in iPad Settings (optional - prevents customers from exiting)</li>
                          <li>When you click "Send to iPad" on a job, it will appear on the iPad screen for signing</li>
                        </ol>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Quote Accepted Confirmation Message */}
                <div className="template-section" style={{ marginTop: '1.5rem' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: '1rem' }}>Quote Accepted - Confirmation Message</CardTitle>
                      <CardDescription>
                        This message is shown to customers after they accept a quote.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor="accepted-title">Title</Label>
                        <Input
                          id="accepted-title"
                          value={settings.email_templates?.quote_accepted_message?.title || 'Quote Accepted'}
                          onChange={(e) => {
                            const updatedTemplates = {
                              ...settings.email_templates,
                              quote_accepted_message: {
                                ...settings.email_templates?.quote_accepted_message,
                                title: e.target.value
                              }
                            };
                            setSettings({ ...settings, email_templates: updatedTemplates });
                          }}
                          data-testid="accepted-title-input"
                        />
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor="accepted-message">Message</Label>
                        <Textarea
                          id="accepted-message"
                          rows={3}
                          value={settings.email_templates?.quote_accepted_message?.message || 'Thank you for accepting the quote. We will be in touch shortly to proceed with the work.'}
                          onChange={(e) => {
                            const updatedTemplates = {
                              ...settings.email_templates,
                              quote_accepted_message: {
                                ...settings.email_templates?.quote_accepted_message,
                                message: e.target.value
                              }
                            };
                            setSettings({ ...settings, email_templates: updatedTemplates });
                          }}
                          data-testid="accepted-message-input"
                        />
                      </div>
                      <div style={{ padding: '0.75rem', backgroundColor: '#d1fae5', borderRadius: '0.375rem' }}>
                        <p style={{ fontSize: '0.75rem', color: '#065f46', marginBottom: '0.25rem' }}><strong>Preview:</strong></p>
                        <p style={{ fontSize: '0.875rem', color: '#059669', fontWeight: '600' }}>
                          {settings.email_templates?.quote_accepted_message?.title || 'Quote Accepted'}
                        </p>
                        <p style={{ fontSize: '0.8rem', color: '#047857' }}>
                          {settings.email_templates?.quote_accepted_message?.message || 'Thank you for accepting the quote. We will be in touch shortly to proceed with the work.'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Quote Declined Confirmation Message */}
                <div className="template-section" style={{ marginTop: '1.5rem' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: '1rem' }}>Quote Declined - Confirmation Message</CardTitle>
                      <CardDescription>
                        This message is shown to customers after they decline a quote.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor="declined-title">Title</Label>
                        <Input
                          id="declined-title"
                          value={settings.email_templates?.quote_declined_message?.title || 'Quote Declined'}
                          onChange={(e) => {
                            const updatedTemplates = {
                              ...settings.email_templates,
                              quote_declined_message: {
                                ...settings.email_templates?.quote_declined_message,
                                title: e.target.value
                              }
                            };
                            setSettings({ ...settings, email_templates: updatedTemplates });
                          }}
                          data-testid="declined-title-input"
                        />
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor="declined-message">Message</Label>
                        <Textarea
                          id="declined-message"
                          rows={3}
                          value={settings.email_templates?.quote_declined_message?.message || 'You have declined this quote. A collection notice has been sent to your email. Please collect your item within 7 days.'}
                          onChange={(e) => {
                            const updatedTemplates = {
                              ...settings.email_templates,
                              quote_declined_message: {
                                ...settings.email_templates?.quote_declined_message,
                                message: e.target.value
                              }
                            };
                            setSettings({ ...settings, email_templates: updatedTemplates });
                          }}
                          data-testid="declined-message-input"
                        />
                      </div>
                      <div style={{ padding: '0.75rem', backgroundColor: '#fee2e2', borderRadius: '0.375rem' }}>
                        <p style={{ fontSize: '0.75rem', color: '#991b1b', marginBottom: '0.25rem' }}><strong>Preview:</strong></p>
                        <p style={{ fontSize: '0.875rem', color: '#dc2626', fontWeight: '600' }}>
                          {settings.email_templates?.quote_declined_message?.title || 'Quote Declined'}
                        </p>
                        <p style={{ fontSize: '0.8rem', color: '#b91c1c' }}>
                          {settings.email_templates?.quote_declined_message?.message || 'You have declined this quote. A collection notice has been sent to your email. Please collect your item within 7 days.'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Job Booked - Signature Request Email */}
                <div className="template-section" style={{ marginTop: '1.5rem' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ fontSize: '1rem' }}>Job Booked - Signature Request Email</CardTitle>
                      <CardDescription>
                        Default subject + message used when emailing the signature request to a customer at job creation. You'll be able to edit it for each customer at send time. Placeholders: {'{customer_name}'}, {'{job_number}'}, {'{business_name}'}, {'{business_phone}'}, {'{business_email}'}, {'{device}'}.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor="job-booked-subject">Email Subject</Label>
                        <Input
                          id="job-booked-subject"
                          value={settings.email_templates?.job_booked_signature?.subject || 'Please Sign Your Job Card - Job #{job_number}'}
                          onChange={(e) => {
                            const updatedTemplates = {
                              ...settings.email_templates,
                              job_booked_signature: {
                                ...settings.email_templates?.job_booked_signature,
                                subject: e.target.value
                              }
                            };
                            setSettings({ ...settings, email_templates: updatedTemplates });
                          }}
                          data-testid="job-booked-template-subject"
                        />
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor="job-booked-body">Email Body</Label>
                        <Textarea
                          id="job-booked-body"
                          rows={14}
                          value={settings.email_templates?.job_booked_signature?.body || ''}
                          onChange={(e) => {
                            const updatedTemplates = {
                              ...settings.email_templates,
                              job_booked_signature: {
                                ...settings.email_templates?.job_booked_signature,
                                body: e.target.value
                              }
                            };
                            setSettings({ ...settings, email_templates: updatedTemplates });
                          }}
                          data-testid="job-booked-template-body"
                          style={{ fontFamily: 'inherit', fontSize: '0.875rem' }}
                        />
                        <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                          The "Review & Sign Job Card" button and job details box are appended automatically below your message.
                        </p>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                        <div>
                          <Label htmlFor="job-booked-default-cc">Default CC (comma-separated)</Label>
                          <Input
                            id="job-booked-default-cc"
                            placeholder="me@mybusiness.com.au"
                            value={settings.email_templates?.job_booked_signature?.default_cc || ''}
                            onChange={(e) => {
                              const updatedTemplates = {
                                ...settings.email_templates,
                                job_booked_signature: {
                                  ...settings.email_templates?.job_booked_signature,
                                  default_cc: e.target.value
                                }
                              };
                              setSettings({ ...settings, email_templates: updatedTemplates });
                            }}
                            data-testid="job-booked-default-cc"
                          />
                          <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                            Auto-fills the CC field on every signature request send.
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="job-booked-default-bcc">Default BCC (comma-separated)</Label>
                          <Input
                            id="job-booked-default-bcc"
                            placeholder="archive@example.com"
                            value={settings.email_templates?.job_booked_signature?.default_bcc || ''}
                            onChange={(e) => {
                              const updatedTemplates = {
                                ...settings.email_templates,
                                job_booked_signature: {
                                  ...settings.email_templates?.job_booked_signature,
                                  default_bcc: e.target.value
                                }
                              };
                              setSettings({ ...settings, email_templates: updatedTemplates });
                            }}
                            data-testid="job-booked-default-bcc"
                          />
                          <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                            Hidden recipient on every signature request. Customer can't see this.
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/settings`, { email_templates: settings.email_templates });
                            toast.success('Signature request template saved');
                          } catch (err) {
                            toast.error('Failed to save template');
                          }
                        }}
                        data-testid="save-job-booked-template"
                        style={{ backgroundColor: '#2563eb', color: 'white' }}
                      >
                        Save Signature Request Template
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Save All Templates Button */}
                <div style={{ marginTop: '1.5rem' }}>
                  <Button
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/settings`, { email_templates: settings.email_templates });
                        toast.success('All templates saved successfully!');
                      } catch (error) {
                        toast.error('Failed to save templates');
                      }
                    }}
                    data-testid="save-all-templates-button"
                  >
                    Save All Templates
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Status Colors Tab */}
            <TabsContent value="colors">
              <div className="status-colors-settings">
                <h3>Status Colors</h3>
                <p className="section-description">
                  Customize the colors for each job status. These colors will be displayed on the dashboard and job lists.
                </p>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
                  {settings.statuses && settings.statuses.map((status) => (
                    <div 
                      key={status} 
                      style={{ 
                        padding: '1rem', 
                        backgroundColor: '#f9fafb', 
                        borderRadius: '0.5rem',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                        <Label style={{ fontWeight: '600', fontSize: '0.95rem' }}>{status}</Label>
                        <div 
                          style={{ 
                            width: '24px', 
                            height: '24px', 
                            borderRadius: '4px', 
                            backgroundColor: settings.status_colors?.[status] || '#6b7280',
                            border: '2px solid #fff',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }} 
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="color"
                          value={settings.status_colors?.[status] || '#6b7280'}
                          onChange={(e) => {
                            const updatedColors = {
                              ...settings.status_colors,
                              [status]: e.target.value
                            };
                            setSettings({ ...settings, status_colors: updatedColors });
                          }}
                          style={{ 
                            width: '50px', 
                            height: '36px', 
                            padding: '2px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          data-testid={`color-picker-${status.toLowerCase().replace(/\s+/g, '-')}`}
                        />
                        <Input
                          value={settings.status_colors?.[status] || '#6b7280'}
                          onChange={(e) => {
                            const updatedColors = {
                              ...settings.status_colors,
                              [status]: e.target.value
                            };
                            setSettings({ ...settings, status_colors: updatedColors });
                          }}
                          placeholder="#000000"
                          style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}
                          data-testid={`color-input-${status.toLowerCase().replace(/\s+/g, '-')}`}
                        />
                      </div>
                      {/* Preview Badge */}
                      <div style={{ marginTop: '0.75rem' }}>
                        <span 
                          style={{ 
                            display: 'inline-block',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            color: '#fff',
                            backgroundColor: settings.status_colors?.[status] || '#6b7280'
                          }}
                        >
                          {status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem' }}>
                  <Button
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/settings`, { status_colors: settings.status_colors });
                        toast.success('Status colors saved successfully!');
                      } catch (error) {
                        toast.error('Failed to save status colors');
                      }
                    }}
                    data-testid="save-status-colors-button"
                  >
                    Save Colors
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const defaultColors = {
                        "Booked": "#3b82f6",
                        "Quoted": "#8b5cf6",
                        "Wait": "#f59e0b",
                        "Approved": "#10b981",
                        "Finish": "#059669",
                        "Parts": "#ec4899",
                        "Not Recoverable": "#6b7280",
                        "Not Proceeding": "#ef4444"
                      };
                      setSettings({ ...settings, status_colors: defaultColors });
                      toast.info('Colors reset to default (not saved yet)');
                    }}
                    data-testid="reset-status-colors-button"
                  >
                    Reset to Default
                  </Button>
                </div>

                {/* Dashboard Card Colors */}
                <div style={{ marginTop: '2.5rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
                  <h3>Dashboard Card Colors</h3>
                  <p className="section-description">
                    Customize the background colors for dashboard summary cards.
                  </p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
                    {/* Total Jobs Color */}
                    <div style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                        <Label style={{ fontWeight: '600', fontSize: '0.95rem' }}>Total Jobs Card</Label>
                        <div style={{ width: '24px', height: '24px', borderRadius: '4px', backgroundColor: settings.dashboard_total_jobs_color || '#10b981', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="color"
                          value={settings.dashboard_total_jobs_color || '#10b981'}
                          onInput={(e) => {
                            // Live preview while dragging
                            setSettings({ ...settings, dashboard_total_jobs_color: e.target.value });
                          }}
                          onChange={async (e) => {
                            // Save when picker closes (final selection)
                            try {
                              await axios.put(`${API}/settings`, { dashboard_total_jobs_color: e.target.value });
                              setSettings({ ...settings, dashboard_total_jobs_color: e.target.value });
                              toast.success('Color saved');
                            } catch (error) {
                              toast.error('Failed to save color');
                            }
                          }}
                          style={{ width: '50px', height: '36px', padding: '2px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
                          data-testid="color-picker-total-jobs"
                        />
                        <Input
                          value={settings.dashboard_total_jobs_color || '#10b981'}
                          onChange={(e) => {
                            setSettings({ ...settings, dashboard_total_jobs_color: e.target.value });
                          }}
                          onBlur={async (e) => {
                            try {
                              await axios.put(`${API}/settings`, { dashboard_total_jobs_color: e.target.value });
                              toast.success('Color saved');
                            } catch (error) {
                              toast.error('Failed to save color');
                            }
                          }}
                          placeholder="#10b981"
                          style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}
                          data-testid="color-input-total-jobs"
                        />
                      </div>
                    </div>

                    {/* Total Revenue Color */}
                    <div style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                        <Label style={{ fontWeight: '600', fontSize: '0.95rem' }}>Total Revenue</Label>
                        <div style={{ width: '24px', height: '24px', borderRadius: '4px', backgroundColor: settings.dashboard_revenue_color || '#16a34a', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="color"
                          value={settings.dashboard_revenue_color || '#16a34a'}
                          onInput={(e) => {
                            // Live preview while dragging
                            setSettings({ ...settings, dashboard_revenue_color: e.target.value });
                          }}
                          onChange={async (e) => {
                            // Save when picker closes (final selection)
                            try {
                              await axios.put(`${API}/settings`, { dashboard_revenue_color: e.target.value });
                              setSettings({ ...settings, dashboard_revenue_color: e.target.value });
                              toast.success('Color saved');
                            } catch (error) {
                              toast.error('Failed to save color');
                            }
                          }}
                          style={{ width: '50px', height: '36px', padding: '2px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
                          data-testid="color-picker-revenue"
                        />
                        <Input
                          value={settings.dashboard_revenue_color || '#16a34a'}
                          onChange={(e) => {
                            setSettings({ ...settings, dashboard_revenue_color: e.target.value });
                          }}
                          onBlur={async (e) => {
                            try {
                              await axios.put(`${API}/settings`, { dashboard_revenue_color: e.target.value });
                              toast.success('Color saved');
                            } catch (error) {
                              toast.error('Failed to save color');
                            }
                          }}
                          placeholder="#16a34a"
                          style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}
                          data-testid="color-input-revenue"
                        />
                      </div>
                    </div>

                    {/* Jobs Completed Color */}
                    <div style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                        <Label style={{ fontWeight: '600', fontSize: '0.95rem' }}>Jobs Completed</Label>
                        <div style={{ width: '24px', height: '24px', borderRadius: '4px', backgroundColor: settings.dashboard_jobs_completed_color || '#2563eb', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="color"
                          value={settings.dashboard_jobs_completed_color || '#2563eb'}
                          onInput={(e) => {
                            // Live preview while dragging
                            setSettings({ ...settings, dashboard_jobs_completed_color: e.target.value });
                          }}
                          onChange={async (e) => {
                            // Save when picker closes (final selection)
                            try {
                              await axios.put(`${API}/settings`, { dashboard_jobs_completed_color: e.target.value });
                              setSettings({ ...settings, dashboard_jobs_completed_color: e.target.value });
                              toast.success('Color saved');
                            } catch (error) {
                              toast.error('Failed to save color');
                            }
                          }}
                          style={{ width: '50px', height: '36px', padding: '2px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
                          data-testid="color-picker-jobs-completed"
                        />
                        <Input
                          value={settings.dashboard_jobs_completed_color || '#2563eb'}
                          onChange={(e) => {
                            setSettings({ ...settings, dashboard_jobs_completed_color: e.target.value });
                          }}
                          onBlur={async (e) => {
                            try {
                              await axios.put(`${API}/settings`, { dashboard_jobs_completed_color: e.target.value });
                              toast.success('Color saved');
                            } catch (error) {
                              toast.error('Failed to save color');
                            }
                          }}
                          placeholder="#2563eb"
                          style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}
                          data-testid="color-input-jobs-completed"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="counter">
              {counter ? (
              <div className="counter-settings">
                <div className="counter-info">
                  <h3>Job Number Counter</h3>
                  <p>Current value: <strong data-testid="current-counter-value">{counter.current_value}</strong></p>
                  <p className="counter-note">The next job created will have job number: {counter.current_value + 1}</p>
                </div>
                <div className="counter-update">
                  <Label htmlFor="new-counter">Set new counter value:</Label>
                  <Input
                    id="new-counter"
                    type="number"
                    defaultValue={counter.current_value}
                    onBlur={(e) => {
                      const value = parseInt(e.target.value);
                      if (value !== counter.current_value && value > 0) {
                        updateCounter(value);
                      }
                    }}
                    data-testid="counter-input"
                  />
                  <p className="counter-warning">⚠️ Warning: Changing this value will affect all new jobs created after this point.</p>
                </div>
              </div>
              ) : (
                <div className="loading">Loading counter...</div>
              )}
            </TabsContent>

            {/* Security Tab - Auto Logout Settings */}
            <TabsContent value="security">
              <div className="security-settings">
                <div style={{ marginBottom: '1.5rem' }}>
                  <TwoFactorPanel />
                </div>
                <Card style={{ marginBottom: '1.5rem' }}>
                  <CardHeader>
                    <CardTitle>Auto-Logout Settings</CardTitle>
                    <CardDescription>Configure automatic logout for all workstations at a scheduled time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {settings ? (
                      <div style={{ display: 'grid', gap: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                          <div>
                            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Enable Auto-Logout</div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Automatically log out all workstations at the scheduled time</div>
                          </div>
                          <Button
                            variant={settings.auto_logout_enabled ? "default" : "outline"}
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/settings`, { auto_logout_enabled: !settings.auto_logout_enabled });
                                setSettings({ ...settings, auto_logout_enabled: !settings.auto_logout_enabled });
                                toast.success(`Auto-logout ${!settings.auto_logout_enabled ? 'enabled' : 'disabled'}`);
                              } catch (error) {
                                toast.error('Failed to update setting');
                              }
                            }}
                            style={{ minWidth: '100px' }}
                          >
                            {settings.auto_logout_enabled ? '✓ Enabled' : 'Disabled'}
                          </Button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div>
                            <Label htmlFor="auto_logout_time">Auto-Logout Time</Label>
                            <Input
                              id="auto_logout_time"
                              type="time"
                              defaultValue={settings.auto_logout_time || '18:30'}
                              onBlur={async (e) => {
                                if (e.target.value && e.target.value !== settings.auto_logout_time) {
                                  try {
                                    await axios.put(`${API}/settings`, { auto_logout_time: e.target.value });
                                    setSettings({ ...settings, auto_logout_time: e.target.value });
                                    toast.success(`Auto-logout time updated to ${e.target.value}`);
                                  } catch (error) {
                                    toast.error('Failed to update setting');
                                    e.target.value = settings.auto_logout_time || '18:30';
                                  }
                                }
                              }}
                            />
                            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                              All workstations will automatically log out at this time
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="auto_logout_warning">Warning Time (minutes)</Label>
                            <Select
                              value={String(settings.auto_logout_warning_minutes || 5)}
                              onValueChange={async (value) => {
                                try {
                                  await axios.put(`${API}/settings`, { auto_logout_warning_minutes: parseInt(value) });
                                  setSettings({ ...settings, auto_logout_warning_minutes: parseInt(value) });
                                  toast.success('Warning time updated');
                                } catch (error) {
                                  toast.error('Failed to update setting');
                                }
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">1 minute</SelectItem>
                                <SelectItem value="2">2 minutes</SelectItem>
                                <SelectItem value="5">5 minutes</SelectItem>
                                <SelectItem value="10">10 minutes</SelectItem>
                                <SelectItem value="15">15 minutes</SelectItem>
                              </SelectContent>
                            </Select>
                            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                              Show warning this many minutes before auto-logout
                            </p>
                          </div>
                        </div>

                        <div style={{ padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <span>⏰</span>
                            <strong>Current Schedule</strong>
                          </div>
                          <p style={{ fontSize: '0.875rem', color: '#92400e' }}>
                            {settings.auto_logout_enabled 
                              ? `All workstations will be logged out at ${settings.auto_logout_time || '18:30'} daily. A ${settings.auto_logout_warning_minutes || 5}-minute warning will be shown before logout.`
                              : 'Auto-logout is currently disabled.'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div>Loading settings...</div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Manual Logout Options</CardTitle>
                    <CardDescription>Log out from current or all devices</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                        <div>
                          <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: '#dc2626' }}>Logout All Devices</div>
                          <div style={{ fontSize: '0.875rem', color: '#7f1d1d' }}>
                            Log out from all workstations immediately. This will require all users to log in again.
                          </div>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive">
                              Logout All Devices
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Logout All Devices?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will immediately log out all workstations, including this one. 
                                All users will need to log in again. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/auth/logout-all`);
                                    toast.success('All devices logged out successfully');
                                    // Force logout current session after a short delay
                                    setTimeout(() => {
                                      localStorage.removeItem('token');
                                      window.location.href = '/';
                                    }, 1500);
                                  } catch (error) {
                                    toast.error('Failed to logout all devices');
                                  }
                                }}
                                style={{ backgroundColor: '#dc2626' }}
                              >
                                Yes, Logout All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Cleanup Mode — suppress automatic customer emails during migration cleanup */}
                {currentUser?.role === 'admin' && (
                  <Card style={{ marginTop: '1.5rem', borderColor: settings?.cleanup_mode_active ? '#f59e0b' : undefined }}>
                    <CardHeader>
                      <CardTitle>
                        🧹 Cleanup Mode
                        <HelpTooltip text="When ON, suppresses automatic customer-facing emails: the Collection Notice that fires on Not Proceeding, plus auto-confirmations triggered by remote signature/quote actions. Manual Send buttons (Send Quote, Email Invoice, etc.) are NOT affected. Useful for tidying carry-over jobs after a migration." />
                      </CardTitle>
                      <CardDescription>
                        Turn this ON when cleaning up imported / migrated jobs so customers don't get a surprise second email. A bright amber banner stays across the top of every page while ON so you can't forget. Click that banner (or this toggle) to turn it off when done.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Switch
                          data-testid="cleanup-mode-toggle"
                          checked={!!settings?.cleanup_mode_active}
                          onCheckedChange={async (checked) => {
                            try {
                              await axios.put(`${API}/settings`, { cleanup_mode_active: checked });
                              setSettings({ ...(settings || {}), cleanup_mode_active: checked });
                              toast.success(checked
                                ? 'Cleanup Mode enabled — automatic customer emails are now suppressed'
                                : 'Cleanup Mode disabled — automatic emails will fire as normal');
                            } catch (e) {
                              toast.error('Failed to update Cleanup Mode');
                            }
                          }}
                        />
                        <strong style={{ fontSize: '0.95rem' }}>
                          {settings?.cleanup_mode_active ? 'ON — automatic emails are being suppressed' : 'OFF — automatic emails fire as normal'}
                        </strong>
                      </div>
                      {settings?.cleanup_mode_active && (
                        <p style={{ fontSize: '0.8rem', color: '#92400e', marginTop: '0.75rem', backgroundColor: '#fef3c7', padding: '0.6rem', borderRadius: '0.375rem', border: '1px solid #fcd34d' }}>
                          🛈 While ON, any job whose status changes to Not Proceeding (manually OR via customer quote rejection) will skip the Collection Notice email. Remote signature & quote-approval confirmations are also suppressed. Each affected job logs a "SUPPRESSED — Cleanup Mode is on" line in its Activity Log so you can manually email any customer who needed contact.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* IP Access Control */}
                {currentUser?.role === 'admin' && (
                  <Card style={{ marginTop: '1.5rem' }}>
                    <CardHeader>
                      <CardTitle>
                        IP Access Control
                        <HelpTooltip 
                          content="Restrict access to your app by IP address. Only whitelisted IPs can access the application. Use bypass code for mobile access from unknown IPs." 
                          side="right"
                        />
                      </CardTitle>
                      <CardDescription>
                        Restrict access to specific IP addresses for enhanced security. Unrecognized IPs will see a "404 Not Found" page.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'grid', gap: '1.5rem' }}>
                        
                        {/* Setup Guide Note */}
                        <div style={{ 
                          padding: '1rem', 
                          backgroundColor: '#eff6ff', 
                          borderRadius: '8px', 
                          border: '1px solid #bfdbfe',
                          borderLeft: '4px solid #3b82f6'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                            <span style={{ fontSize: '1.25rem' }}>📋</span>
                            <div>
                              <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '0.5rem' }}>
                                Setup Guide - When You're Ready to Deploy
                              </div>
                              <div style={{ fontSize: '0.875rem', color: '#1e40af', lineHeight: '1.6' }}>
                                <p style={{ marginBottom: '0.5rem' }}>
                                  <strong>Note:</strong> In the preview environment, the IP shown below may be a proxy IP, not your real IP. 
                                  Once deployed to your own domain, you'll see your actual IP address.
                                </p>
                                <p style={{ marginBottom: '0.5rem' }}><strong>After deployment, follow these steps:</strong></p>
                                <ol style={{ marginLeft: '1.25rem', marginBottom: '0' }}>
                                  <li>Check "Your Current IP Address" shows your <strong>real home/office IP</strong></li>
                                  <li>Click "+ Add This IP" from your <strong>home</strong></li>
                                  <li>Go to <strong>work</strong> and add that IP too</li>
                                  <li>Set up a <strong>Bypass Code</strong> for mobile access</li>
                                  <li><strong>Bookmark</strong> the bypass URL on your phone</li>
                                  <li>Finally, <strong>enable IP Restriction</strong></li>
                                </ol>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Enable/Disable Toggle */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', backgroundColor: ipAccessSettings.enabled ? '#f0fdf4' : '#f9fafb', borderRadius: '8px', border: `1px solid ${ipAccessSettings.enabled ? '#86efac' : '#e5e7eb'}` }}>
                          <div>
                            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                              {ipAccessSettings.enabled ? '🔒 IP Restriction Enabled' : '🔓 IP Restriction Disabled'}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                              {ipAccessSettings.enabled 
                                ? 'Only whitelisted IPs can access the application' 
                                : 'Anyone can access the application (login still required)'}
                            </div>
                          </div>
                          <Button
                            variant={ipAccessSettings.enabled ? "default" : "outline"}
                            onClick={() => {
                              setIpAccessSettings({ ...ipAccessSettings, enabled: !ipAccessSettings.enabled });
                            }}
                            style={{ minWidth: '100px' }}
                          >
                            {ipAccessSettings.enabled ? '✓ Enabled' : 'Disabled'}
                          </Button>
                        </div>

                        {/* Current IP Display */}
                        <div style={{ padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                              <div style={{ fontSize: '0.875rem', color: '#0369a1', marginBottom: '0.25rem' }}>Your Current IP Address:</div>
                              <div style={{ fontWeight: '600', fontFamily: 'monospace', fontSize: '1.125rem' }}>{currentIP || 'Loading...'}</div>
                            </div>
                            <Button variant="outline" size="sm" onClick={handleAddCurrentIP}>
                              + Add This IP
                            </Button>
                          </div>
                        </div>

                        {/* Allowed IPs List */}
                        <div>
                          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                            Allowed IP Addresses ({ipAccessSettings.allowed_ips?.length || 0})
                          </h4>
                          
                          {ipAccessSettings.allowed_ips?.length > 0 ? (
                            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                              {ipAccessSettings.allowed_ips.map((ipEntry, index) => (
                                <div 
                                  key={index}
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between', 
                                    padding: '0.75rem 1rem',
                                    borderBottom: index < ipAccessSettings.allowed_ips.length - 1 ? '1px solid #e5e7eb' : 'none',
                                    backgroundColor: ipEntry.ip === currentIP ? '#f0fdf4' : 'white'
                                  }}
                                >
                                  <div>
                                    <div style={{ fontFamily: 'monospace', fontWeight: '500' }}>
                                      {ipEntry.ip}
                                      {ipEntry.ip === currentIP && (
                                        <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#16a34a', fontFamily: 'sans-serif' }}>
                                          (Current)
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{ipEntry.label}</div>
                                  </div>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleRemoveIP(ipEntry.ip)}
                                    style={{ color: '#dc2626' }}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '8px', color: '#6b7280' }}>
                              No IP addresses whitelisted yet. Add your IP address above.
                            </div>
                          )}

                          {/* Add New IP */}
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                            <Input
                              placeholder="IP Address (e.g., 192.168.1.1)"
                              value={newIPAddress}
                              onChange={(e) => setNewIPAddress(e.target.value)}
                              style={{ flex: 1 }}
                            />
                            <Input
                              placeholder="Label (e.g., Office)"
                              value={newIPLabel}
                              onChange={(e) => setNewIPLabel(e.target.value)}
                              style={{ flex: 1 }}
                            />
                            <Button onClick={handleAddIP}>Add IP</Button>
                          </div>
                        </div>

                        {/* Bypass Code Section */}
                        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                            <div>
                              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                                Mobile/Remote Access Bypass
                                <HelpTooltip 
                                  content="When you're on an unknown IP (like mobile), add ?access=YourCode to the URL to bypass IP restrictions. Bookmark this URL for easy mobile access." 
                                  side="right"
                                />
                              </h4>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                Allow access from unknown IPs using a secret code
                              </div>
                            </div>
                            <Button
                              variant={ipAccessSettings.bypass_enabled ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                setIpAccessSettings({ ...ipAccessSettings, bypass_enabled: !ipAccessSettings.bypass_enabled });
                              }}
                            >
                              {ipAccessSettings.bypass_enabled ? '✓ Enabled' : 'Disabled'}
                            </Button>
                          </div>

                          {ipAccessSettings.bypass_enabled && (
                            <div style={{ display: 'grid', gap: '1rem' }}>
                              <div>
                                <Label>Bypass Code</Label>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <Input
                                    type="text"
                                    placeholder="Enter a secret code"
                                    value={ipAccessSettings.bypass_code}
                                    onChange={(e) => setIpAccessSettings({ ...ipAccessSettings, bypass_code: e.target.value })}
                                  />
                                  <Button 
                                    variant="outline" 
                                    onClick={() => {
                                      const code = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 6);
                                      setIpAccessSettings({ ...ipAccessSettings, bypass_code: code });
                                    }}
                                  >
                                    Generate
                                  </Button>
                                </div>
                              </div>

                              {ipAccessSettings.bypass_code && (
                                <div style={{ padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                                  <div style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '0.5rem' }}>
                                    <strong>Your Bypass URL:</strong>
                                  </div>
                                  <div style={{ 
                                    fontFamily: 'monospace', 
                                    fontSize: '0.875rem', 
                                    padding: '0.5rem', 
                                    backgroundColor: 'white', 
                                    borderRadius: '4px',
                                    wordBreak: 'break-all'
                                  }}>
                                    {window.location.origin}?access={ipAccessSettings.bypass_code}
                                  </div>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    style={{ marginTop: '0.5rem' }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(`${window.location.origin}?access=${ipAccessSettings.bypass_code}`);
                                      toast.success('Bypass URL copied to clipboard');
                                    }}
                                  >
                                    📋 Copy URL
                                  </Button>
                                  <p style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '0.5rem' }}>
                                    Bookmark this URL on your phone for easy access from anywhere.
                                  </p>
                                </div>
                              )}

                              {/* Remember Device Option */}
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                                <div>
                                  <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>Remember Device</div>
                                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                    After using bypass code, remember device for {ipAccessSettings.remember_days} days
                                  </div>
                                </div>
                                <Button
                                  variant={ipAccessSettings.remember_device ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => {
                                    setIpAccessSettings({ ...ipAccessSettings, remember_device: !ipAccessSettings.remember_device });
                                  }}
                                >
                                  {ipAccessSettings.remember_device ? '✓ On' : 'Off'}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Save Button */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                          <Button 
                            onClick={handleSaveIPSettings}
                            disabled={savingIPSettings}
                            style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' }}
                          >
                            {savingIPSettings ? 'Saving...' : '💾 Save IP Settings'}
                          </Button>
                        </div>

                        {/* Warning */}
                        {ipAccessSettings.enabled && ipAccessSettings.allowed_ips?.length === 0 && (
                          <div style={{ padding: '1rem', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#dc2626' }}>
                              <span>⚠️</span>
                              <strong>Warning:</strong>
                            </div>
                            <p style={{ fontSize: '0.875rem', color: '#7f1d1d', marginTop: '0.25rem' }}>
                              IP restriction is enabled but no IPs are whitelisted. Add your current IP address above, otherwise you may be locked out!
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Job Sticker Settings */}
                <Card style={{ marginTop: '1.5rem' }}>
                  <CardHeader>
                    <CardTitle>Job Sticker Settings</CardTitle>
                    <CardDescription>Configure the size of job stickers for label printing (e.g., Zebra GC420d)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {settings ? (
                      <div style={{ display: 'grid', gap: '1.5rem' }}>
                        {/* Sticker Dimensions */}
                        <div>
                          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>Label Dimensions</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div>
                              <Label htmlFor="sticker_width">Width (mm)</Label>
                              <Input
                                id="sticker_width"
                                type="number"
                                min="10"
                                max="200"
                                defaultValue={settings.sticker_width_mm || 40}
                                onBlur={async (e) => {
                                  const value = parseInt(e.target.value) || 40;
                                  if (value !== settings.sticker_width_mm) {
                                    try {
                                      await axios.put(`${API}/settings`, { sticker_width_mm: value });
                                      setSettings({ ...settings, sticker_width_mm: value });
                                      toast.success('Sticker width updated');
                                    } catch (error) {
                                      toast.error('Failed to update setting');
                                      e.target.value = settings.sticker_width_mm || 40;
                                    }
                                  }
                                }}
                                data-testid="sticker-width-input"
                              />
                            </div>
                            <div>
                              <Label htmlFor="sticker_height">Height (mm)</Label>
                              <Input
                                id="sticker_height"
                                type="number"
                                min="10"
                                max="200"
                                defaultValue={settings.sticker_height_mm || 28}
                                onBlur={async (e) => {
                                  const value = parseInt(e.target.value) || 28;
                                  if (value !== settings.sticker_height_mm) {
                                    try {
                                      await axios.put(`${API}/settings`, { sticker_height_mm: value });
                                      setSettings({ ...settings, sticker_height_mm: value });
                                      toast.success('Sticker height updated');
                                    } catch (error) {
                                      toast.error('Failed to update setting');
                                      e.target.value = settings.sticker_height_mm || 28;
                                    }
                                  }
                                }}
                                data-testid="sticker-height-input"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Font Sizes */}
                        <div>
                          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>Font Sizes (pt)</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                            <div>
                              <Label htmlFor="font_size_job">Job Number</Label>
                              <Input
                                id="font_size_job"
                                type="number"
                                min="6"
                                max="24"
                                defaultValue={settings.sticker_font_size_job || 12}
                                onBlur={async (e) => {
                                  const value = parseInt(e.target.value) || 12;
                                  if (value !== settings.sticker_font_size_job) {
                                    try {
                                      await axios.put(`${API}/settings`, { sticker_font_size_job: value });
                                      setSettings({ ...settings, sticker_font_size_job: value });
                                      toast.success('Font size updated');
                                    } catch (error) {
                                      toast.error('Failed to update setting');
                                      e.target.value = settings.sticker_font_size_job || 12;
                                    }
                                  }
                                }}
                                data-testid="font-size-job-input"
                              />
                            </div>
                            <div>
                              <Label htmlFor="font_size_name">Customer Name</Label>
                              <Input
                                id="font_size_name"
                                type="number"
                                min="6"
                                max="24"
                                defaultValue={settings.sticker_font_size_name || 12}
                                onBlur={async (e) => {
                                  const value = parseInt(e.target.value) || 12;
                                  if (value !== settings.sticker_font_size_name) {
                                    try {
                                      await axios.put(`${API}/settings`, { sticker_font_size_name: value });
                                      setSettings({ ...settings, sticker_font_size_name: value });
                                      toast.success('Font size updated');
                                    } catch (error) {
                                      toast.error('Failed to update setting');
                                      e.target.value = settings.sticker_font_size_name || 12;
                                    }
                                  }
                                }}
                                data-testid="font-size-name-input"
                              />
                            </div>
                            <div>
                              <Label htmlFor="font_size_phone">Phone Number</Label>
                              <Input
                                id="font_size_phone"
                                type="number"
                                min="6"
                                max="24"
                                defaultValue={settings.sticker_font_size_phone || 12}
                                onBlur={async (e) => {
                                  const value = parseInt(e.target.value) || 12;
                                  if (value !== settings.sticker_font_size_phone) {
                                    try {
                                      await axios.put(`${API}/settings`, { sticker_font_size_phone: value });
                                      setSettings({ ...settings, sticker_font_size_phone: value });
                                      toast.success('Font size updated');
                                    } catch (error) {
                                      toast.error('Failed to update setting');
                                      e.target.value = settings.sticker_font_size_phone || 12;
                                    }
                                  }
                                }}
                                data-testid="font-size-phone-input"
                              />
                            </div>
                          </div>
                        </div>

                        <BrowserPrintSettings settings={settings} />

                        {/* Direct USB Printing (bypasses Windows driver) */}
                        <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', backgroundColor: '#f8fafc' }}>
                          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                            Direct USB Printing (Beta)
                            <HelpTooltip text="Send raw EPL commands straight to the Zebra over USB — bypasses Chrome's print dialog and the Windows driver entirely. Use this if normal printing produces duplicate or wrongly-rotated labels. Chrome/Edge only." />
                          </h4>
                          <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.75rem' }}>
                            Pair your Zebra once, then enable the toggle. Future Print Sticker clicks will skip the print dialog and send raw commands directly to the printer.
                          </p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <Button
                              type="button"
                              variant="outline"
                              data-testid="pair-zebra-button"
                              onClick={async () => {
                                if (!webUsbSupported()) {
                                  toast.error('WebUSB not supported. Use Chrome or Edge.');
                                  return;
                                }
                                try {
                                  const device = await pairZebraPrinter(false);
                                  toast.success(`Paired: ${device.productName || 'Zebra printer'}`);
                                } catch (e) {
                                  if (e.name !== 'NotFoundError') {
                                    toast.error(e.message || 'Pairing cancelled');
                                  }
                                }
                              }}
                            >
                              🔌 Pair Zebra Printer
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              data-testid="pair-any-usb-button"
                              onClick={async () => {
                                if (!webUsbSupported()) {
                                  toast.error('WebUSB not supported. Use Chrome or Edge.');
                                  return;
                                }
                                try {
                                  const device = await pairZebraPrinter(true);
                                  toast.success(`Paired: ${device.productName || 'USB device'} (VID 0x${device.vendorId.toString(16)} PID 0x${device.productId.toString(16)})`);
                                } catch (e) {
                                  if (e.name !== 'NotFoundError') {
                                    toast.error(e.message || 'Pairing cancelled');
                                  }
                                }
                              }}
                              title="Show all USB devices (useful if the Zebra-only filter shows nothing)"
                            >
                              Pair Any USB…
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              data-testid="check-paired-zebra-button"
                              onClick={async () => {
                                const d = await getPairedZebra();
                                if (d) toast.success(`Already paired: ${d.productName || 'Zebra'} (VID 0x${d.vendorId.toString(16)})`);
                                else toast.info('No Zebra paired yet');
                              }}
                            >
                              Check Status
                            </Button>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                data-testid="use-direct-usb-checkbox"
                                checked={!!settings.sticker_use_direct_usb}
                                onChange={async (e) => {
                                  const val = e.target.checked;
                                  try {
                                    await axios.put(`${API}/settings`, { sticker_use_direct_usb: val });
                                    setSettings({ ...settings, sticker_use_direct_usb: val });
                                    toast.success(val ? 'Direct USB printing enabled' : 'Direct USB printing disabled');
                                  } catch (error) {
                                    toast.error('Failed to update setting');
                                  }
                                }}
                              />
                              Use Direct USB for Print Sticker
                            </label>
                          </div>
                        </div>

                        {/* Orientation / Rotation */}
                        <div>
                          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                            Print Rotation
                            <HelpTooltip text="If your printer feeds the label sideways and prints across 2 pages or at the wrong size, try changing this. Print a test sticker after each change to find what works." />
                          </h4>
                          <Select
                            value={String(settings.sticker_rotation_deg ?? 0)}
                            onValueChange={async (value) => {
                              const deg = parseInt(value, 10) || 0;
                              try {
                                await axios.put(`${API}/settings`, { sticker_rotation_deg: deg });
                                setSettings({ ...settings, sticker_rotation_deg: deg });
                                toast.success('Sticker rotation updated');
                              } catch (error) {
                                toast.error('Failed to update setting');
                              }
                            }}
                          >
                            <SelectTrigger data-testid="sticker-rotation-select" style={{ maxWidth: '320px' }}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0° — Landscape (default)</SelectItem>
                              <SelectItem value="90">90° — Portrait, rotated clockwise</SelectItem>
                              <SelectItem value="180">180° — Landscape, upside-down</SelectItem>
                              <SelectItem value="270">270° — Portrait, rotated counter-clockwise</SelectItem>
                            </SelectContent>
                          </Select>
                          <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.5rem' }}>
                            Most Zebra GC420d setups use 0° or 90°. Try the "Print Test Sticker" button below after each change.
                          </p>
                        </div>

                        {/* Print Test Sticker */}
                        <div>
                          <Button
                            type="button"
                            variant="outline"
                            data-testid="print-test-sticker-button"
                            onClick={() =>
                              printJobSticker(
                                { job_number: 'TEST', customer_name: 'Test Customer', mobile: '0400 000 000' },
                                settings
                              )
                            }
                          >
                            🏷️ Print Test Sticker
                          </Button>
                        </div>

                        {/* Preview */}
                        <div style={{ padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            <span>🏷️</span>
                            <strong>Sticker Preview</strong>
                          </div>
                          <div style={{ 
                            border: '1px dashed #0369a1', 
                            padding: '8px', 
                            backgroundColor: 'white',
                            display: 'inline-block',
                            minWidth: '120px'
                          }}>
                            <div style={{ fontSize: `${settings.sticker_font_size_job || 12}px`, fontWeight: 'bold' }}>Job #12345</div>
                            <div style={{ fontSize: `${settings.sticker_font_size_name || 12}px`, fontWeight: 'bold' }}>John Smith</div>
                            <div style={{ fontSize: `${settings.sticker_font_size_phone || 12}px`, fontWeight: 'bold' }}>0412 345 678</div>
                          </div>
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.75rem' }}>
                            Label size: {settings.sticker_width_mm || 40}mm × {settings.sticker_height_mm || 28}mm
                          </p>
                          <p style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.5rem', fontWeight: '500' }}>
                            ⚠️ In Chrome print dialog: Click "More settings" and uncheck "Headers and footers" to remove date/URL from labels
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div>Loading settings...</div>
                    )}
                  </CardContent>
                </Card>

                {/* Printer Preferences */}
                <Card style={{ marginTop: '1.5rem' }}>
                  <CardHeader>
                    <CardTitle>Printer Preferences</CardTitle>
                    <CardDescription>
                      Enter the friendly name of each printer (as it appears in your OS print dialog). Every time you click a Print button, the preview will display a clear "Send to: [your printer]" reminder at the top — so you never pick the wrong printer in the dialog.
                      <br/>
                      <span style={{ fontSize: '0.75rem', color: '#92400e' }}>
                        Note: web browsers don't allow apps to pick printers automatically. You still click the printer in the print dialog, but the banner stops you guessing.
                      </span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {settings ? (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
                        <div>
                          <Label htmlFor="printer_sticker">Sticker Printer</Label>
                          <Input
                            id="printer_sticker"
                            type="text"
                            placeholder="e.g. Zebra GC420d"
                            defaultValue={settings.printer_sticker || ''}
                            onBlur={async (e) => {
                              const value = e.target.value.trim();
                              if (value !== (settings.printer_sticker || '')) {
                                try {
                                  await axios.put(`${API}/settings`, { printer_sticker: value });
                                  setSettings({ ...settings, printer_sticker: value });
                                  toast.success('Sticker printer updated');
                                } catch (error) {
                                  toast.error('Failed to update setting');
                                  e.target.value = settings.printer_sticker || '';
                                }
                              }
                            }}
                            data-testid="printer-sticker-input"
                          />
                          <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>Used for the small job stickers</p>
                        </div>
                        <div>
                          <Label htmlFor="printer_job_card">Job Card / Reports Printer</Label>
                          <Input
                            id="printer_job_card"
                            type="text"
                            placeholder="e.g. HP LaserJet M404"
                            defaultValue={settings.printer_job_card || ''}
                            onBlur={async (e) => {
                              const value = e.target.value.trim();
                              if (value !== (settings.printer_job_card || '')) {
                                try {
                                  await axios.put(`${API}/settings`, { printer_job_card: value });
                                  setSettings({ ...settings, printer_job_card: value });
                                  toast.success('Job card printer updated');
                                } catch (error) {
                                  toast.error('Failed to update setting');
                                  e.target.value = settings.printer_job_card || '';
                                }
                              }
                            }}
                            data-testid="printer-job-card-input"
                          />
                          <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>Used for job cards, expense reports, P&L, stock summary</p>
                        </div>
                        <div>
                          <Label htmlFor="printer_invoice">Invoice Printer</Label>
                          <Input
                            id="printer_invoice"
                            type="text"
                            placeholder="e.g. Canon TS9550"
                            defaultValue={settings.printer_invoice || ''}
                            onBlur={async (e) => {
                              const value = e.target.value.trim();
                              if (value !== (settings.printer_invoice || '')) {
                                try {
                                  await axios.put(`${API}/settings`, { printer_invoice: value });
                                  setSettings({ ...settings, printer_invoice: value });
                                  toast.success('Invoice printer updated');
                                } catch (error) {
                                  toast.error('Failed to update setting');
                                  e.target.value = settings.printer_invoice || '';
                                }
                              }
                            }}
                            data-testid="printer-invoice-input"
                          />
                          <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>Used when you print or "Print Invoice"</p>
                        </div>
                      </div>
                    ) : (
                      <div>Loading settings...</div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Address Lookup Tab */}
            <TabsContent value="address">
              <div className="address-settings">
                <Card>
                  <CardHeader>
                    <CardTitle>Google Places API Configuration</CardTitle>
                    <CardDescription>
                      Enable address autocomplete in the Add Job form.
                      Get your API key from <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Google Cloud Console</a>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Current Status */}
                    {addressSettings?.has_api_key && (
                      <div style={{
                        padding: '1rem',
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #86efac',
                        borderRadius: '0.5rem'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                          <span style={{
                            backgroundColor: '#22c55e',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>Connected</span>
                          <span style={{ fontSize: '0.875rem' }}>API Key: {addressSettings.api_key_masked}</span>
                        </div>
                        <Button 
                          variant="outline" 
                          onClick={handleTestAddressConnection}
                          disabled={testingAddressConnection}
                          data-testid="test-address-connection-btn"
                        >
                          {testingAddressConnection ? 'Testing...' : 'Test Connection'}
                        </Button>
                      </div>
                    )}

                    {/* Add/Update API Key */}
                    <div className="space-y-4">
                      <h4 style={{ fontWeight: '500' }}>{addressSettings?.has_api_key ? 'Update API Key' : 'Add API Key'}</h4>
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                          <Input
                            type="password"
                            placeholder="Enter your Google Places API key"
                            value={addressApiKey}
                            onChange={(e) => setAddressApiKey(e.target.value)}
                            data-testid="address-api-key-input"
                          />
                        </div>
                        <Button 
                          onClick={handleSaveAddressApiKey}
                          disabled={savingAddressSettings || !addressApiKey.trim()}
                          data-testid="save-address-api-key-btn"
                        >
                          {savingAddressSettings ? 'Saving...' : 'Save API Key'}
                        </Button>
                      </div>
                      <p style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        Your API key is stored securely and never displayed in full after saving.
                      </p>
                    </div>

                    {/* Help Section */}
                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '1.5rem', marginTop: '1.5rem' }}>
                      <h4 style={{ fontWeight: '500', marginBottom: '1rem' }}>How to get your Google Places API Key</h4>
                      <ol style={{ listStyle: 'decimal', paddingLeft: '1.5rem', fontSize: '0.875rem', color: '#64748b', lineHeight: '1.8' }}>
                        <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Google Cloud Console</a></li>
                        <li>Create a new project (or select existing one)</li>
                        <li>Go to "APIs & Services" → "Library"</li>
                        <li>Search for and enable <strong>"Places API"</strong></li>
                        <li>Go to "APIs & Services" → "Credentials"</li>
                        <li>Click "Create Credentials" → "API Key"</li>
                        <li>Copy the key and paste it above</li>
                        <li><strong>Important:</strong> Restrict the key to "Places API" for security</li>
                      </ol>
                      <div style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        backgroundColor: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: '0.5rem'
                      }}>
                        <p style={{ fontSize: '0.875rem', margin: 0 }}>
                          <strong>Free Tier:</strong> Google provides $200/month free credit, which covers approximately 10,000 address lookups - plenty for most small businesses!
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* SMS (Twilio) Tab */}
            <TabsContent value="sms">
              <SMSSettingsTab settings={settings} setSettings={setSettings} API={API} />
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications">
              <div className="notifications-settings">
                {/* Booked (Provisional) Transaction Reminders */}
                <Card style={{ marginBottom: '1.5rem' }}>
                  <CardHeader>
                    <CardTitle>
                      Booked Transaction Reminders
                      <HelpTooltip
                        content="A Purchase or Expense with status 'Booked' is a provisional item — for example, a prepaid AusPost shipping label or a parts pre-order. Once it's older than the threshold below, it appears on your Dashboard reminder widget so you don't forget to convert it to 'Finished' when the job is done."
                        side="right"
                      />
                    </CardTitle>
                    <CardDescription>
                      Set how long a provisional (Booked) purchase or expense can sit before appearing on the Dashboard stale-reminder widget.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {settings && (
                      <div style={{ display: 'grid', gap: '1rem', maxWidth: '520px' }}>
                        <div>
                          <Label htmlFor="booked-reminder-days" style={{ fontWeight: 600 }}>
                            Reminder threshold (days)
                          </Label>
                          <Input
                            id="booked-reminder-days"
                            type="number"
                            min="1"
                            max="365"
                            value={settings.booked_transaction_reminder_days || 30}
                            onChange={(e) => updateSettings('booked_transaction_reminder_days', parseInt(e.target.value) || 30)}
                            data-testid="booked-reminder-days"
                            style={{ width: '160px', marginTop: '0.35rem' }}
                          />
                          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.5rem' }}>
                            Any <strong>Booked</strong> Purchase or Expense older than this many days will show up on the Dashboard's stale-reminder widget.
                          </p>
                        </div>
                        <div style={{ padding: '0.85rem 1rem', backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', fontSize: '0.85rem', color: '#78350f' }}>
                          <strong>Tip:</strong> Booked items are <em>excluded</em> from your P&L and Reports until you flip them to <strong>Finished</strong> — perfect for tracking money already spent (labels, parts, pre-orders) that hasn't yet been realised against a job.
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Browser Notifications */}
                <Card style={{ marginBottom: '1.5rem' }}>
                  <CardHeader>
                    <CardTitle>
                      Browser Notifications
                      <HelpTooltip 
                        content="Enable browser notifications to get alerts for overdue jobs and upcoming reminders even when the app is in the background." 
                        side="right"
                      />
                    </CardTitle>
                    <CardDescription>Get notified about overdue jobs and reminders in your browser</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {settings && (
                      <div style={{ display: 'grid', gap: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', backgroundColor: settings.browser_notifications_enabled ? '#f0fdf4' : '#f9fafb', borderRadius: '8px', border: `1px solid ${settings.browser_notifications_enabled ? '#86efac' : '#e5e7eb'}` }}>
                          <div>
                            <div style={{ fontWeight: '600' }}>Browser Notifications</div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                              {settings.browser_notifications_enabled ? '🔔 Notifications are enabled' : '🔕 Notifications are disabled'}
                            </div>
                          </div>
                          <Button 
                            variant={settings.browser_notifications_enabled ? 'destructive' : 'default'}
                            onClick={async () => {
                              // Request permission if enabling
                              if (!settings.browser_notifications_enabled && 'Notification' in window) {
                                const permission = await Notification.requestPermission();
                                if (permission !== 'granted') {
                                  toast.error('Please allow notifications in your browser settings');
                                  return;
                                }
                              }
                              try {
                                await axios.put(`${API}/settings`, { browser_notifications_enabled: !settings.browser_notifications_enabled });
                                setSettings({ ...settings, browser_notifications_enabled: !settings.browser_notifications_enabled });
                                toast.success(settings.browser_notifications_enabled ? 'Notifications disabled' : 'Notifications enabled');
                              } catch (error) {
                                toast.error('Failed to update setting');
                              }
                            }}
                            data-testid="toggle-browser-notifications-btn"
                          >
                            {settings.browser_notifications_enabled ? 'Disable' : 'Enable'}
                          </Button>
                        </div>
                        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          When enabled, you'll receive browser notifications for overdue jobs and reminders due today.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Daily Email Digest */}
                <Card>
                  <CardHeader>
                    <CardTitle>Daily Email Digest</CardTitle>
                    <CardDescription>Receive a daily summary of jobs due and reminders</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {settings && (
                      <div style={{ display: 'grid', gap: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', backgroundColor: settings.daily_digest_enabled ? '#f0fdf4' : '#f9fafb', borderRadius: '8px', border: `1px solid ${settings.daily_digest_enabled ? '#86efac' : '#e5e7eb'}` }}>
                          <div>
                            <div style={{ fontWeight: '600' }}>Daily Digest Email</div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                              {settings.daily_digest_enabled ? '📧 Daily digest is enabled' : '📭 Daily digest is disabled'}
                            </div>
                          </div>
                          <Button 
                            variant={settings.daily_digest_enabled ? 'destructive' : 'default'}
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/settings`, { daily_digest_enabled: !settings.daily_digest_enabled });
                                setSettings({ ...settings, daily_digest_enabled: !settings.daily_digest_enabled });
                                toast.success(settings.daily_digest_enabled ? 'Daily digest disabled' : 'Daily digest enabled');
                              } catch (error) {
                                toast.error('Failed to update setting');
                              }
                            }}
                            data-testid="toggle-daily-digest-btn"
                          >
                            {settings.daily_digest_enabled ? 'Disable' : 'Enable'}
                          </Button>
                        </div>

                        {settings.daily_digest_enabled && (
                          <>
                            <div>
                              <Label htmlFor="digest_time">Send Time</Label>
                              <Input
                                id="digest_time"
                                type="time"
                                defaultValue={settings.daily_digest_time || '07:00'}
                                onBlur={async (e) => {
                                  if (e.target.value !== settings.daily_digest_time) {
                                    try {
                                      await axios.put(`${API}/settings`, { daily_digest_time: e.target.value });
                                      setSettings({ ...settings, daily_digest_time: e.target.value });
                                      toast.success('Digest time updated');
                                    } catch (error) {
                                      toast.error('Failed to update');
                                    }
                                  }
                                }}
                                style={{ width: '150px' }}
                                data-testid="digest-time-input"
                              />
                              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                Time to send daily digest (24-hour format)
                              </p>
                            </div>

                            <div>
                              <Label htmlFor="digest_recipients">Recipients (one email per line)</Label>
                              <textarea
                                id="digest_recipients"
                                defaultValue={(settings.daily_digest_recipients || []).join('\n')}
                                onBlur={async (e) => {
                                  const recipients = e.target.value.split('\n').map(r => r.trim()).filter(r => r);
                                  try {
                                    await axios.put(`${API}/settings`, { daily_digest_recipients: recipients });
                                    setSettings({ ...settings, daily_digest_recipients: recipients });
                                    toast.success('Recipients updated');
                                  } catch (error) {
                                    toast.error('Failed to update');
                                  }
                                }}
                                style={{ width: '100%', minHeight: '100px', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e5e7eb', fontFamily: 'inherit' }}
                                placeholder="email1@example.com&#10;email2@example.com"
                                data-testid="digest-recipients-input"
                              />
                            </div>

                            <div style={{ display: 'flex', gap: '1rem' }}>
                              <Button
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    const response = await axios.post(`${API}/notifications/send-daily-digest`);
                                    toast.success(response.data.message);
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Failed to send digest');
                                  }
                                }}
                                data-testid="send-digest-now-btn"
                              >
                                Send Digest Now
                              </Button>
                              <span style={{ fontSize: '0.875rem', color: '#6b7280', alignSelf: 'center' }}>
                                Test by sending the digest immediately
                              </span>
                            </div>
                          </>
                        )}

                        <div style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                          <h4 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>What's included in the digest?</h4>
                          <ul style={{ fontSize: '0.875rem', color: '#64748b', paddingLeft: '1.25rem', margin: 0 }}>
                            <li>⚠️ Overdue jobs</li>
                            <li>📅 Jobs due today</li>
                            <li>🔔 Reminders due today</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Users Tab - Admin Only */}
            {currentUser?.role === 'admin' && (
              <TabsContent value="audit">
                <SecurityAuditTab API={API} />
              </TabsContent>
            )}

            {/* Users Tab - Admin Only */}
            {currentUser?.role === 'admin' && (
              <TabsContent value="users">
                <div className="users-management">
                  <div className="users-header">
                    <h3>User Management</h3>
                    <Button onClick={() => setShowUserForm(true)}>Add New User</Button>
                  </div>

                  {(showUserForm || editingUser) && (
                    <Card style={{ marginBottom: '2rem' }}>
                      <CardHeader>
                        <CardTitle>{editingUser ? 'Edit User' : 'Create New User'}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="user-form">
                          <div className="form-field">
                            <Label htmlFor="username">Username</Label>
                            <Input
                              id="username"
                              value={userForm.username}
                              onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                              required
                              disabled={editingUser}
                            />
                          </div>

                          <div className="form-field">
                            <Label htmlFor="full_name">Full Name</Label>
                            <Input
                              id="full_name"
                              value={userForm.full_name}
                              onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                              required
                            />
                          </div>

                          <div className="form-field">
                            <Label htmlFor="password">Password {editingUser && '(leave blank to keep current)'}</Label>
                            <Input
                              id="password"
                              type="password"
                              value={userForm.password}
                              onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                              required={!editingUser}
                              minLength={6}
                              placeholder="Minimum 6 characters"
                            />
                          </div>

                          <div className="form-field">
                            <Label htmlFor="role">Role</Label>
                            <Select
                              value={userForm.role}
                              onValueChange={(value) => setUserForm({ ...userForm, role: value })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="field-description">
                              • <strong>Admin:</strong> Full access including Reports and User Management<br/>
                              • <strong>User:</strong> Access to all features except Reports
                            </p>
                          </div>

                          <div className="form-actions">
                            <Button type="submit">{editingUser ? 'Update User' : 'Create User'}</Button>
                            <Button type="button" variant="outline" onClick={cancelUserEdit}>
                              Cancel
                            </Button>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  )}

                  <Card>
                    <CardHeader>
                      <CardTitle>All Users</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Username</TableHead>
                            <TableHead>Full Name</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {users.map(user => (
                            <TableRow key={user.id}>
                              <TableCell>{user.username}</TableCell>
                              <TableCell>{user.full_name}</TableCell>
                              <TableCell>
                                <span className={`role-badge ${user.role}`}>
                                  {user.role}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => startEditUser(user)}
                                  >
                                    Edit
                                  </Button>
                                  {user.id !== currentUser.id && (
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => handleDeleteUser(user.id)}
                                    >
                                      Delete
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            )}

            {/* Backup & Restore Tab */}
            {currentUser?.role === 'admin' && (
              <TabsContent value="backup">
                <div className="backup-settings" style={{ display: 'grid', gap: '1.5rem' }}>
                  {/* Manual Backup Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        Manual Backup
                        <HelpTooltip 
                          content="Create a full backup of all your data including jobs, customers, settings, and attachments. Store backups securely off-site." 
                          side="right"
                        />
                      </CardTitle>
                      <CardDescription>Create and download backups on demand</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        <Button 
                          onClick={() => handleDownloadBackup()} 
                          disabled={backupLoading}
                          data-testid="download-backup-btn"
                        >
                          {backupLoading ? 'Creating...' : '⬇️ Download Backup Now'}
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => handleCreateBackup(false)} 
                          disabled={backupLoading}
                          data-testid="create-backup-btn"
                        >
                          💾 Save to Server
                        </Button>
                        {backupSettings?.email_backup_address && (
                          <Button 
                            variant="outline"
                            onClick={() => handleCreateBackup(true)} 
                            disabled={backupLoading}
                            data-testid="backup-email-btn"
                          >
                            📧 Create & Email Backup
                          </Button>
                        )}
                      </div>
                      
                      {backupSettings?.last_backup_time && (
                        <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f0fdf4', borderRadius: '0.5rem', fontSize: '0.875rem' }}>
                          <strong>Last Backup:</strong> {formatBackupDate(backupSettings.last_backup_time)} 
                          {backupSettings.last_backup_size && ` (${formatBytes(backupSettings.last_backup_size)})`}
                          {backupSettings.last_backup_status && ` - ${backupSettings.last_backup_status}`}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Automatic Backup Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Automatic Backup Schedule</CardTitle>
                      <CardDescription>Configure daily automatic backups</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'grid', gap: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <Label style={{ minWidth: '120px' }}>Enable Auto Backup</Label>
                          <input
                            type="checkbox"
                            checked={backupSettings?.auto_backup_enabled || false}
                            onChange={(e) => handleUpdateBackupSettings({ auto_backup_enabled: e.target.checked })}
                            style={{ width: '20px', height: '20px' }}
                            data-testid="auto-backup-toggle"
                          />
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <Label style={{ minWidth: '120px' }}>Backup Time</Label>
                          <Input
                            type="time"
                            value={backupSettings?.auto_backup_time || '02:00'}
                            onChange={(e) => handleUpdateBackupSettings({ auto_backup_time: e.target.value })}
                            style={{ width: '150px' }}
                            disabled={!backupSettings?.auto_backup_enabled}
                            data-testid="backup-time-input"
                          />
                          <span style={{ fontSize: '0.875rem', color: '#666' }}>Daily at this time</span>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <Label style={{ minWidth: '120px' }}>Retention Days</Label>
                          <Input
                            type="number"
                            min="1"
                            max="365"
                            value={backupSettings?.auto_backup_retention_days ?? 30}
                            onChange={(e) => setBackupSettings(prev => ({ ...prev, auto_backup_retention_days: e.target.value }))}
                            onBlur={(e) => { const n = parseInt(e.target.value, 10); if (n >= 1 && n <= 365) handleUpdateBackupSettings({ auto_backup_retention_days: n }); else fetchBackupSettings(); }}
                            style={{ width: '100px' }}
                            data-testid="retention-days-input"
                          />
                          <span style={{ fontSize: '0.875rem', color: '#666' }}>Old backups auto-deleted</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Email Delivery Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Email Backup Delivery</CardTitle>
                      <CardDescription>Receive backup files via email</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'grid', gap: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <Label style={{ minWidth: '120px' }}>Enable Email</Label>
                          <input
                            type="checkbox"
                            checked={backupSettings?.email_backup_enabled || false}
                            onChange={(e) => handleUpdateBackupSettings({ email_backup_enabled: e.target.checked })}
                            style={{ width: '20px', height: '20px' }}
                            data-testid="email-backup-toggle"
                          />
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <Label style={{ minWidth: '120px' }}>Email Address</Label>
                          <Input
                            type="email"
                            placeholder="backup@example.com"
                            value={backupSettings?.email_backup_address || ''}
                            onChange={(e) => setBackupSettings(prev => ({ ...prev, email_backup_address: e.target.value }))}
                            onBlur={(e) => handleUpdateBackupSettings({ email_backup_address: e.target.value.trim() })}
                            style={{ maxWidth: '300px' }}
                            data-testid="backup-email-input"
                          />
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleTestBackupEmail}
                            disabled={!backupSettings?.email_backup_address || backupLoading}
                          >
                            Test Email
                          </Button>
                        </div>
                        
                        <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                          Note: Email requires SMTP to be configured in Email Config tab.
                        </p>

                        {/* Notification email — sent after every automatic backup
                            (success or failure) so the user gets proactive
                            confirmation each morning without logging in. */}
                        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px dashed #e5e7eb' }}>
                          <Label style={{ display: 'block', marginBottom: '0.35rem' }}>
                            Notification Email (auto-backup status)
                          </Label>
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                          <Input
                            type="email"
                            placeholder="alerts@yourbusiness.com"
                            value={backupSettings?.backup_notification_email || ''}
                            onChange={(e) => setBackupSettings(prev => ({ ...prev, backup_notification_email: e.target.value }))}
                            onBlur={(e) => handleUpdateBackupSettings({ backup_notification_email: e.target.value.trim() })}
                            data-testid="backup-notification-email-input"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleTestBackupNotification}
                            disabled={backupLoading}
                            style={{ whiteSpace: 'nowrap' }}
                            data-testid="backup-test-notification-button"
                          >
                            Send Test Notification
                          </Button>
                          </div>
                          <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.35rem' }}>
                            Receives a ✅ success summary or ❌ failure alert after every scheduled backup.
                            Leave blank to fall back to the Email Backup address above, then your business email.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Restore Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Restore from Backup</CardTitle>
                      <CardDescription>Restore your data from a backup file</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'grid', gap: '1rem' }}>
                        <div style={{ padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '0.5rem', border: '1px solid #f59e0b' }}>
                          <strong style={{ color: '#92400e' }}>⚠️ Warning:</strong>
                          <span style={{ color: '#92400e', marginLeft: '0.5rem' }}>
                            Restoring will replace your current data. This action cannot be undone.
                          </span>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                          <input
                            type="file"
                            accept=".zip"
                            onChange={handleFileUpload}
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                          />
                          <Button 
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            data-testid="upload-backup-btn"
                          >
                            📁 Upload Backup File
                          </Button>
                          {restoreFile && (
                            <span style={{ fontSize: '0.875rem', color: '#666' }}>
                              Selected: {restoreFile.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Google Drive Integration Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>☁️</span>
                        <span>Google Drive Auto-Backup</span>
                        {driveStatus.connected && (
                          <span style={{
                            fontSize: '0.75rem',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '9999px',
                            backgroundColor: '#dcfce7',
                            color: '#166534',
                            fontWeight: '600'
                          }}>
                            Connected
                          </span>
                        )}
                      </CardTitle>
                      <CardDescription>
                        Automatically upload every backup ZIP to your Google Drive. You can also manually upload existing backups.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {!driveStatus.connected ? (
                        <div>
                          <div style={{
                            padding: '1rem',
                            backgroundColor: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '0.5rem',
                            marginBottom: '1rem',
                            fontSize: '0.875rem',
                            color: '#1e40af'
                          }}>
                            <strong>How it works:</strong> Click "Connect Google Drive" → sign in with the Google account that owns the destination Drive → grant permission → all future backups will auto-upload to a folder named below.
                          </div>
                          <Button
                            onClick={handleDriveConnect}
                            style={{ backgroundColor: '#4285f4', color: 'white' }}
                            data-testid="drive-connect-btn"
                          >
                            ☁️ Connect Google Drive
                          </Button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                          <div style={{
                            padding: '0.75rem',
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #86efac',
                            borderRadius: '0.5rem',
                            fontSize: '0.875rem',
                            color: '#166534'
                          }}>
                            ✓ Connected{driveStatus.account_email ? ` as ${driveStatus.account_email}` : ''}
                          </div>

                          <div>
                            <Label htmlFor="drive-folder">Drive Folder Name</Label>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                              <Input
                                id="drive-folder"
                                value={driveFolderInput}
                                onChange={(e) => setDriveFolderInput(e.target.value)}
                                placeholder="Emergent Backup"
                                data-testid="drive-folder-input"
                              />
                            </div>
                            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                              Folder will be created automatically if it doesn't exist.
                              Note: with the <code>drive.file</code> scope, this app only sees folders/files it has created itself.
                            </p>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              id="drive-auto-upload"
                              checked={!!driveStatus.auto_upload}
                              onChange={(e) => setDriveStatus({ ...driveStatus, auto_upload: e.target.checked })}
                              data-testid="drive-auto-upload-checkbox"
                            />
                            <Label htmlFor="drive-auto-upload" style={{ margin: 0, cursor: 'pointer' }}>
                              Automatically upload every new backup to Google Drive
                            </Label>
                          </div>

                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <Button
                              onClick={handleDriveConfigSave}
                              disabled={driveSaving}
                              data-testid="drive-save-btn"
                            >
                              {driveSaving ? 'Saving...' : 'Save Drive Settings'}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={handleDriveDisconnect}
                              style={{ color: '#dc2626' }}
                              data-testid="drive-disconnect-btn"
                            >
                              Disconnect Drive
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Backup History Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Backup History</CardTitle>
                      <CardDescription>Server-stored backups available for download or restore</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {backupList.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Size</TableHead>
                              <TableHead>Records</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {backupList.map((backup) => {
                              const isFailed = backup.status === 'failed';
                              return (
                              <TableRow key={backup.id} style={isFailed ? { backgroundColor: '#fef2f2' } : undefined}>
                                <TableCell>
                                  {isFailed && <span style={{ marginRight: '0.4rem' }} title={backup.error || 'Backup failed'}>❌</span>}
                                  {formatBackupDate(backup.created_at)}
                                  {isFailed && backup.error && (
                                    <div style={{ fontSize: '0.7rem', color: '#b91c1c', marginTop: '0.15rem' }}>
                                      {backup.error.length > 80 ? backup.error.slice(0, 80) + '…' : backup.error}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <span style={{ 
                                    padding: '0.25rem 0.5rem', 
                                    borderRadius: '0.25rem',
                                    backgroundColor: isFailed
                                      ? '#fee2e2'
                                      : (backup.backup_type === 'automatic' ? '#dbeafe' : '#f0fdf4'),
                                    fontSize: '0.75rem'
                                  }}>
                                    {isFailed ? `${backup.backup_type} (failed)` : backup.backup_type}
                                  </span>
                                </TableCell>
                                <TableCell>{formatBytes(backup.size_bytes)}</TableCell>
                                <TableCell>
                                  {Object.values(backup.record_counts || {}).reduce((a, b) => a + b, 0)} total
                                </TableCell>
                                <TableCell>
                                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleDownloadBackup(backup.id)}
                                      disabled={!backup.file_exists}
                                      title="Download backup"
                                      data-testid={`download-backup-${backup.id}`}
                                    >
                                      ⬇️
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleCopyDownloadLink(backup.id)}
                                      disabled={!backup.file_exists}
                                      title="Copy download link"
                                      data-testid={`copy-link-${backup.id}`}
                                    >
                                      📋
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleRestoreFromServer(backup.id)}
                                      disabled={!backup.file_exists}
                                      title="Restore from this backup"
                                    >
                                      🔄
                                    </Button>
                                    <Button 
                                      variant="destructive" 
                                      size="sm"
                                      onClick={() => handleDeleteBackup(backup.id)}
                                      title="Delete backup"
                                    >
                                      🗑️
                                    </Button>
                                    {driveStatus.connected && backup.file_exists && (
                                      backup.drive_uploaded ? (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => backup.drive_link && window.open(backup.drive_link, '_blank')}
                                          title={`Uploaded to Drive${backup.drive_link ? ' — click to view' : ''}`}
                                          style={{ color: '#166534' }}
                                        >
                                          ☁️✓
                                        </Button>
                                      ) : (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleManualDriveUpload(backup.id)}
                                          disabled={driveUploadingId === backup.id}
                                          title="Upload to Google Drive"
                                          data-testid={`drive-upload-${backup.id}`}
                                        >
                                          {driveUploadingId === backup.id ? '...' : '☁️'}
                                        </Button>
                                      )
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      ) : (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                          No backups stored on server yet. Create your first backup above.
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Database Reset Section */}
                  <Card style={{ borderColor: '#dc2626', borderWidth: '2px' }}>
                    <CardHeader>
                      <CardTitle style={{ color: '#dc2626' }}>⚠️ Database Reset</CardTitle>
                      <CardDescription>Clear all business data for a fresh start (use before going live)</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ 
                        padding: '1rem', 
                        backgroundColor: '#fef2f2', 
                        borderRadius: '0.5rem', 
                        marginBottom: '1rem',
                        border: '1px solid #fecaca'
                      }}>
                        <strong style={{ color: '#dc2626' }}>⚠️ Warning:</strong>
                        <span style={{ color: '#991b1b', marginLeft: '0.5rem' }}>
                          This will permanently delete ALL jobs, customers, suppliers, stock items, expenses, and purchases. 
                          Settings and user accounts will be kept. This action cannot be undone.
                        </span>
                      </div>

                      {/* Critical Backup Reminder */}
                      <div style={{ 
                        padding: '1rem', 
                        backgroundColor: '#fffbeb', 
                        borderRadius: '0.5rem', 
                        marginBottom: '1rem',
                        border: '1px solid #fcd34d'
                      }}>
                        <strong style={{ color: '#92400e', display: 'block', marginBottom: '0.25rem' }}>
                          💾 BEFORE you reset — download a backup to your computer!
                        </strong>
                        <span style={{ color: '#78350f', fontSize: '0.875rem' }}>
                          The reset also deletes all <strong>server-stored backup files</strong> from disk. 
                          To be able to restore later, you must <strong>download the backup ZIP to your local machine</strong> first 
                          (Backup &amp; Restore tab → "Download" button on the latest backup). 
                          You can later use <strong>"Restore from Upload"</strong> to bring it back.
                        </span>
                      </div>

                      {/* Current Database Stats */}
                      {dbStats && (
                        <div style={{ 
                          padding: '1rem', 
                          backgroundColor: '#f8fafc', 
                          borderRadius: '0.5rem',
                          marginBottom: '1rem'
                        }}>
                          <h4 style={{ marginBottom: '0.75rem', fontWeight: '600' }}>Current Database Contents:</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', fontSize: '0.875rem' }}>
                            <div>Jobs: <strong>{dbStats.jobs || 0}</strong></div>
                            <div>Customers: <strong>{dbStats.customers || 0}</strong></div>
                            <div>Suppliers: <strong>{dbStats.suppliers || 0}</strong></div>
                            <div>Stock Items: <strong>{dbStats.stock_items || 0}</strong></div>
                            <div>Expenses: <strong>{dbStats.expenses || 0}</strong></div>
                            <div>Purchases: <strong>{dbStats.purchases || 0}</strong></div>
                          </div>
                          <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #e2e8f0' }}>
                            <strong>Total Records: {dbStats.total_records || 0}</strong>
                            <span style={{ marginLeft: '1rem', color: '#666' }}>
                              Current Job #: {dbStats.current_job_number || 0}
                            </span>
                          </div>
                        </div>
                      )}

                      <Button 
                        variant="destructive"
                        onClick={() => setShowResetConfirm(true)}
                        data-testid="reset-database-btn"
                      >
                        🗑️ Reset Database
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Database Reset Confirmation Dialog */}
                <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle style={{ color: '#dc2626' }}>⚠️ Confirm Database Reset</AlertDialogTitle>
                      <AlertDialogDescription>
                        <div style={{ marginBottom: '1rem', color: '#dc2626', fontWeight: '500' }}>
                          This will permanently delete ALL business data:
                        </div>
                        <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem', color: '#666' }}>
                          <li>All jobs and job history</li>
                          <li>All customers</li>
                          <li>All suppliers</li>
                          <li>All stock items</li>
                          <li>All expenses</li>
                          <li>All purchases</li>
                          <li>All backup history <strong>(including server-stored backup files on disk)</strong></li>
                        </ul>
                        <div style={{ 
                          marginBottom: '1rem', 
                          padding: '0.75rem', 
                          backgroundColor: '#fffbeb', 
                          border: '1px solid #fcd34d',
                          borderRadius: '0.5rem'
                        }}>
                          <strong style={{ color: '#92400e' }}>💾 Did you download a backup?</strong>
                          <div style={{ color: '#78350f', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                            Server-stored backups will also be deleted. Make sure you have a 
                            <strong> ZIP file downloaded to your computer</strong> if you want to restore later.
                          </div>
                        </div>
                        <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f0fdf4', borderRadius: '0.5rem' }}>
                          <strong style={{ color: '#166534' }}>✓ Will be kept:</strong> Settings, User accounts, Job counter reset to start
                        </div>
                        
                        <div style={{ marginBottom: '1rem' }}>
                          <Label style={{ display: 'block', marginBottom: '0.5rem' }}>
                            Enter your admin password:
                          </Label>
                          <Input
                            type="password"
                            value={resetPassword}
                            onChange={(e) => setResetPassword(e.target.value)}
                            placeholder="Admin password"
                            data-testid="reset-password-input"
                          />
                        </div>
                        
                        <div>
                          <Label style={{ display: 'block', marginBottom: '0.5rem' }}>
                            Type <strong style={{ color: '#dc2626' }}>RESET</strong> to confirm:
                          </Label>
                          <Input
                            type="text"
                            value={resetConfirmText}
                            onChange={(e) => setResetConfirmText(e.target.value.toUpperCase())}
                            placeholder="Type RESET"
                            data-testid="reset-confirm-input"
                          />
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel onClick={() => {
                        setShowResetConfirm(false);
                        setResetPassword('');
                        setResetConfirmText('');
                      }}>
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDatabaseReset}
                        disabled={resetLoading || resetConfirmText !== 'RESET' || !resetPassword}
                        style={{ backgroundColor: '#dc2626' }}
                        data-testid="confirm-reset-btn"
                      >
                        {resetLoading ? 'Resetting...' : '🗑️ Reset Database'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                {/* Restore Confirmation Dialog */}
                <AlertDialog open={showRestoreConfirm} onOpenChange={setShowRestoreConfirm}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirm Restore</AlertDialogTitle>
                      <AlertDialogDescription>
                        <div style={{ marginBottom: '1rem' }}>
                          <strong style={{ color: '#dc2626' }}>⚠️ Warning:</strong> This will replace your current data with the backup data.
                        </div>
                        
                        <div style={{ marginBottom: '1rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={selectiveRestore}
                              onChange={(e) => setSelectiveRestore(e.target.checked)}
                            />
                            <span>Selective restore (choose specific data types)</span>
                          </label>
                        </div>
                        
                        {selectiveRestore && (
                          <div style={{ 
                            padding: '1rem', 
                            backgroundColor: '#f8fafc', 
                            borderRadius: '0.5rem',
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '0.5rem'
                          }}>
                            {BACKUP_COLLECTIONS.map((collection) => (
                              <label key={collection} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedCollections.includes(collection)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedCollections([...selectedCollections, collection]);
                                    } else {
                                      setSelectedCollections(selectedCollections.filter(c => c !== collection));
                                    }
                                  }}
                                />
                                <span style={{ textTransform: 'capitalize' }}>{collection.replace('_', ' ')}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel onClick={() => {
                        setShowRestoreConfirm(false);
                        setSelectedBackupId(null);
                        setRestoreFile(null);
                        setSelectiveRestore(false);
                        setSelectedCollections([]);
                      }}>
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={confirmRestore}
                        disabled={backupLoading || (selectiveRestore && selectedCollections.length === 0)}
                        style={{ backgroundColor: '#dc2626' }}
                      >
                        {backupLoading ? 'Restoring...' : 'Restore Data'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </TabsContent>
            )}

            {/* Jim2 Cutover Tab */}
            {currentUser?.role === 'admin' && (
              <TabsContent value="cutover">
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle style={{ color: '#b91c1c' }}>⚠️ Jim2 Cutover Migration</CardTitle>
                      <CardDescription>
                        One-time tool to wipe all transactional data and re-import customers + jobs from the latest Jim2 Excel exports.
                        Settings, users, templates, status colours and Drive credentials are preserved.
                      </CardDescription>
                    </CardHeader>
                    <CardContent style={{ display: 'grid', gap: '1.25rem' }}>
                      <div style={{ padding: '0.75rem 1rem', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 6, fontSize: '0.85rem', color: '#78350f' }}>
                        <strong>Before you click Run:</strong>
                        <ol style={{ margin: '0.5rem 0 0 1.25rem', paddingLeft: 0 }}>
                          <li>Take a fresh backup (Backup &amp; Restore tab).</li>
                          <li>Export the latest <code>customers_v2.xlsx</code> and <code>jobs.xlsx</code> from Jim2.</li>
                          <li>Upload both files below.</li>
                          <li>Type <code>{cutoverStatus?.confirmation_phrase || 'WIPE AND IMPORT'}</code> in the confirmation box.</li>
                          <li>Click Run. This may take 5–15 minutes for ~14k customers / ~18k jobs. Do not refresh.</li>
                        </ol>
                      </div>

                      {/* File uploads */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '-0.25rem' }}>
                        <Label style={{ fontWeight: 600 }}>Upload Jim2 exports (3 files)</Label>
                        {(cutoverStatus?.customers?.present || cutoverStatus?.jobs?.present || cutoverStatus?.referrals?.present) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleCutoverClear}
                            disabled={cutoverRunning || cutoverUploadingKind !== null}
                            data-testid="cutover-clear-btn"
                          >
                            🗑️ Clear staged files
                          </Button>
                        )}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#78350f', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '0.5rem 0.75rem' }}>
                        <strong>Tip:</strong> Three Jim2 exports are needed because they each carry different fields:
                        <ol style={{ margin: '0.25rem 0 0 1.25rem', paddingLeft: 0 }}>
                          <li><strong>Customers</strong> — the 49-column file with sheet <em>"Enter your data here"</em>. Has Phone/Mobile/Email/Address Line 1/2/3/Suburb/State/Post Code/First Name/Last Name.</li>
                          <li><strong>Jobs</strong> — sheet <em>"Full job card"</em>. Has Job#, Status, Cust#, Fault Description, Date In, totals etc.</li>
                          <li><strong>Customer Referrals</strong> — the 48-column "Card File" export with sheet <em>"Sheet1"</em>. Has the <em>Report Level 1</em> column with "Google", "Walk-in", "Partner" etc. <em>(Optional — referrals stay blank if omitted.)</em></li>
                        </ol>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                        {[
                          { kind: 'customers', label: 'Customers (49-col with contact details)', testid: 'cutover-customers', required: true },
                          { kind: 'jobs', label: 'Jobs (Full job card)', testid: 'cutover-jobs', required: true },
                          { kind: 'referrals', label: 'Customer Referrals (Card File w/ Report Level 1)', testid: 'cutover-referrals', required: false },
                        ].map(({ kind, label, testid, required }) => {
                          const info = cutoverStatus?.[kind];
                          return (
                            <div key={kind} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '0.75rem' }}>
                              <Label style={{ fontWeight: 600 }}>
                                {label} {!required && <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>}
                              </Label>
                              <div style={{ marginTop: '0.5rem' }}>
                                <input
                                  type="file"
                                  accept=".xlsx"
                                  data-testid={`${testid}-file-input`}
                                  disabled={cutoverUploadingKind !== null || cutoverRunning}
                                  onChange={(e) => handleCutoverUpload(kind, e.target.files?.[0])}
                                />
                              </div>
                              <div style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
                                {cutoverUploadingKind === kind ? (
                                  <span style={{ color: '#6b7280' }}>Uploading...</span>
                                ) : info?.present ? (
                                  <span style={{ color: '#16a34a' }} data-testid={`${testid}-present`}>
                                    ✅ Uploaded · {(info.size / 1024).toFixed(0)} KB · {new Date(info.modified).toLocaleString()}
                                  </span>
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>{required ? 'No file uploaded yet' : 'Not uploaded (referrals will be blank)'}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Confirmation + Run */}
                      <div style={{ display: 'grid', gap: '0.5rem' }}>
                        <Label>Type <code>{cutoverStatus?.confirmation_phrase || 'WIPE AND IMPORT'}</code> to confirm</Label>
                        <Input
                          value={cutoverConfirmText}
                          onChange={(e) => setCutoverConfirmText(e.target.value)}
                          placeholder={cutoverStatus?.confirmation_phrase || 'WIPE AND IMPORT'}
                          disabled={cutoverRunning}
                          data-testid="cutover-confirm-input"
                        />
                        <Button
                          onClick={() => handleCutoverRun('full')}
                          disabled={
                            cutoverRunning ||
                            !cutoverStatus?.customers?.present ||
                            !cutoverStatus?.jobs?.present ||
                            cutoverConfirmText !== (cutoverStatus?.confirmation_phrase || 'WIPE AND IMPORT')
                          }
                          style={{ backgroundColor: '#dc2626', color: '#fff' }}
                          data-testid="cutover-run-btn"
                        >
                          {cutoverRunning ? 'Running migration… (this can take ~10 minutes)' : '🔴 Run FULL Cutover (wipes data + re-imports)'}
                        </Button>
                        <Button
                          onClick={() => handleCutoverRun('backfills_only')}
                          disabled={
                            cutoverRunning ||
                            !cutoverStatus?.customers?.present ||
                            cutoverConfirmText !== (cutoverStatus?.confirmation_phrase || 'WIPE AND IMPORT')
                          }
                          variant="outline"
                          style={{ borderColor: '#2563eb', color: '#2563eb' }}
                          data-testid="cutover-run-backfills-btn"
                        >
                          {cutoverRunning ? 'Running…' : '🔁 Re-run BACKFILLS only (referral, types, revenue dates — non-destructive)'}
                        </Button>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          Use <strong>Backfills only</strong> if the previous run got partway through and you want to retry just the 4 enrichment steps without wiping your imported customers and jobs.
                        </div>
                      </div>

                      {/* Result viewer */}
                      {cutoverResult && (
                        <div data-testid="cutover-result" style={{ display: 'grid', gap: '0.75rem' }}>
                          <div style={{ fontWeight: 600, color: cutoverResult.running ? '#2563eb' : (cutoverResult.ok ? '#16a34a' : '#dc2626') }}>
                            {cutoverResult.running
                              ? `⏳ Running… current step: ${cutoverResult.current_step || 'starting…'}`
                              : cutoverResult.ok
                                ? '✅ Migration finished successfully'
                                : `❌ Failed at: ${cutoverResult.failed_at || 'unknown step'}`}
                          </div>
                          {(cutoverResult.steps || []).map((s) => (
                            <details key={s.step} open={s.exit_code !== 0}>
                              <summary style={{ cursor: 'pointer', fontWeight: 500 }}>
                                {s.exit_code === 0 ? '✅' : '❌'} {s.step}
                                {' '}<span style={{ color: '#6b7280', fontWeight: 400 }}>
                                  (exit {s.exit_code}{typeof s.duration_s === 'number' ? `, ${s.duration_s}s` : ''})
                                </span>
                              </summary>
                              {s.stdout_tail && (
                                <pre style={{ background: '#0b1020', color: '#c5d1eb', padding: '0.75rem', borderRadius: 6, maxHeight: 240, overflow: 'auto', fontSize: '0.75rem' }}>{s.stdout_tail}</pre>
                              )}
                              {s.stderr_tail && (
                                <pre style={{ background: '#3b0a0a', color: '#fecaca', padding: '0.75rem', borderRadius: 6, maxHeight: 240, overflow: 'auto', fontSize: '0.75rem' }}>{s.stderr_tail}</pre>
                              )}
                            </details>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
